/* review-workspace-panels.jsx — review workspace context tabs + Flywheel gate.
   Ported from Viper `handoff-review-page` (`be2-panels.jsx`, `be2-comments.jsx`,
   `be2-history.jsx`, `be2-flywheel.jsx`) 2026-09-03 — ADAPTED to the live app:
   html-backed regions, certified-QA attribution, scoped Chase proposals, and
   in-session comments/drafts/contribution choices. */

import { compareAssetManifests } from '../lib/revision-compare.js';
import { chaseMinorScope, chaseQuickPrompts, chaseAcceptLabel, chaseProposalSet } from '../lib/review-chase-ui.js';
window.GooseReviewChase = { chaseProposalSet };

const RW_SWATCHES = ['#FBC600', '#3B3C43', '#34657F', '#1B1B1B', '#4F9D69', '#FFFFFF'];

function RW_BoundBackground({blockId,field,setProp,assetNames,assetsBase,assetContext}) {
  const [error,setError]=React.useState(null);
  let value;try{value=JSON.parse(field.value);}catch{return <div className="w-bar-note">Background source cannot be interpreted safely. Use Code.</div>;}
  const disabled=!field.editable||!assetNames;
  const change=changes=>{const result=setProp(blockId,field.k,JSON.stringify({...value,...changes}));setError(result?.ok?null:result?.error);};
  const imageName=value.src.replace(/^(?:\.\/)?images\//,'');
  return <section className="w-bound-background" data-manual-background={field.k} data-manual-certainty={field.certainty}>
    <div className="w-sec-h">{value.enabled?'Background image':'Add a background'}</div>
    {value.enabled&&assetsBase&&<div className="w-bg-top"><img className="w-bg-thumb" src={rwAssetUrl(assetsBase,imageName)} alt="Background asset"/><span className="w-bg-t"><b>{imageName}</b><i>Background controls · {field.l.split(' · ')[0]}</i></span><button className="w-chip" disabled={disabled} onClick={()=>change({enabled:false,src:''})}>Remove</button></div>}
    <select className="w-in" aria-label="Background image asset" disabled={disabled} value={value.enabled?imageName:''} onChange={e=>{if(e.target.value)change({enabled:true,src:'images/'+e.target.value});}}>
      <option value="">Choose a bundled image</option>{value.enabled&&!assetNames?.includes(imageName)&&<option value={imageName}>{imageName}</option>}{(assetNames||[]).map(name=><option key={name}>{name}</option>)}
    </select>
    {assetContext&&<RW_AssetsSection {...assetContext} onlyNames={value.enabled?[imageName]:[]} inline />}
    <div className="w-bg-row"><span className="w-bg-l">Fit</span><div className="w-seg sm">{[['cover','Cover'],['contain','Contain'],['tile','Tile']].map(([key,label])=><button type="button" key={key} disabled={disabled||!value.enabled} className={value.fit===key?'on':''} onClick={()=>change({fit:key})}>{label}</button>)}</div></div>
    <div className="w-bg-row"><span className="w-bg-l">Fallback</span><div className="w-swatches">{RW_SWATCHES.map(color=><button type="button" key={color} title={color} aria-label={'Background fallback '+color} className={rwSwatchSelected(value.color,color)?'on':''} aria-pressed={rwSwatchSelected(value.color,color)} disabled={disabled} style={{background:color}} onClick={()=>change({color})}/>)}</div></div>
    <details className="w-field-source"><summary>Background source & limitations</summary><p className="w-fhint">Changes {field.l.split(' · ')[0]} only. Unspecified CSS uses editor defaults, not variant rules. Child fills may cover it; Outlook/VML code is not rewritten. Apply changes reruns full-email QA.</p></details>
    {!field.editable&&<div className="w-bar-note">Apply or revert the current style edit to refresh these controls.</div>}
    {error&&<div role="alert" className="w-callout minor">{error}</div>}
  </section>;
}

function rwSwatchSelected(value, color) {
  const hex = String(value || '').trim().toUpperCase();
  return (/^#[0-9A-F]{3}$/.test(hex) ? '#' + [...hex.slice(1)].map(c => c + c).join('') : hex) === color;
}

function RW_BoundField({ blockId, field, setProp, assetNames, assetContext }) {
  const [draft, setDraft] = React.useState(String(field.value ?? ''));
  const [error, setError] = React.useState(null);
  React.useEffect(() => {setDraft(String(field.value ?? ''));setError(null);}, [field.value]);
  const commit = (value = draft) => {
    const result = setProp(blockId, field.k, value);
    setError(result && !result.ok ? result.error : null);
  };
  const id = 'manual-' + blockId + '-' + field.k;
  const input = { id, 'aria-label': field.l, 'data-manual-field': field.k, className: 'w-in', value: draft,
    disabled: !field.editable, 'aria-invalid': !!error, onChange: e => setDraft(e.target.value),
    onBlur: () => commit(), onKeyDown: e => {if (e.key === 'Enter' && field.t !== 'ta') {e.preventDefault();commit();}} };
  return <div className="w-bound-field" data-manual-certainty={field.certainty}>
    <label className="w-lbl" htmlFor={id}>{field.l.split(' · ').at(-1)}</label>
    {field.t === 'color' ? <div className="w-swatches">{field.editable && RW_SWATCHES.map(color => <button type="button" key={color} title={color} aria-label={field.l + ' ' + color} className={rwSwatchSelected(draft,color)?'on':''} aria-pressed={rwSwatchSelected(draft,color)} style={{ background: color }} onClick={() => {setDraft(color);commit(color);}} />)}<input {...input} type="text" /></div>
    : field.validation === 'local-asset' ? <select {...input} disabled={!field.editable || !assetNames} onChange={e => {setDraft(e.target.value);commit(e.target.value);}}>
      <option value={draft}>{draft}</option>{(assetNames || []).filter(name => !draft.endsWith('/' + name)).map(name => <option key={name} value={'images/' + name}>{name}</option>)}
    </select> : field.options ? <select {...input} onChange={e => {setDraft(e.target.value);commit(e.target.value);}}>
      {field.options.map(value => <option key={value} value={value}>{value}</option>)}
    </select> : field.t === 'ta' ? <textarea {...input} rows={3} /> : field.t === 'num' ? <div className="w-row" data-manual-number={field.k}>
      <span className="w-unit"><input {...input} type="number" min={field.min} max={field.max} step="any" title={`${field.min}–${field.max} ${field.u || 'unitless'}`} /><i>{field.u || ''}</i></span>
      {field.editable && <input className="w-range" type="range" aria-label={field.l + ' slider'} min={field.min} max={field.max} step="1"
        value={Number.isFinite(Number(draft)) ? draft : field.value} onChange={e => { setDraft(e.target.value);commit(e.target.value); }} />}
    </div> : <input {...input} type="text" />}
    {field.validation==='local-asset'&&assetContext&&<RW_AssetsSection {...assetContext} onlyNames={[draft.replace(/^(?:\.\/)?images\//,'')]} inline placementFields={assetContext.b.fields} sourceField={field} />}
    {field.t==='num'&&/_width(?:_attribute)?$/.test(field.k)&&<div className="w-width-hint">{field.u==='%'?<button type="button" className="w-chip" disabled={!field.editable} onClick={()=>{setDraft('100');commit('100');}}>Full · 100%</button>:<span>Fixed width · {field.u||'HTML pixels'}. Use Code to change width mode.</span>}</div>}
    <details className="w-field-source"><summary>Source · {field.l.split(' · ')[0]}</summary><small className="w-meta">{field.editable ? 'Exact HTML value · draft override, not a variant rule' : 'Protected value · edit through the governed code/ESP path'}</small></details>
    {error && <div role="alert" className="w-callout minor">{error}</div>}
  </div>;
}

function RW_Fields2({ b, setProp, job, session, assetContext }) {
  const [assetNames, setAssetNames] = React.useState(null);
  React.useEffect(() => {
    let alive=true;setAssetNames(null);
    if (job?.id && session?.revision_digest && window.GooseAPI?.listAssets) window.GooseAPI.listAssets(job.id).then(data => {
      if (alive && data?.revision === session.revision && data.revision_digest === session.revision_digest) setAssetNames((data.assets || []).filter(a => /^image\//.test(a.mime || '') || /\.(png|jpe?g|gif|webp)$/i.test(a.name)).map(a => a.name));
    }).catch(() => {});
    return () => {alive=false;};
  }, [job?.id,session?.revision_digest]);
  // Legacy descriptors have no byte-bound setter. Never display them as
  // working controls or substitute a synthetic block recipe.
  const allFields=Object.values(b.fields || {}).filter(field => field?.schema === 'ArtifactField.v1');
  const fields=allFields.filter(field=>field.t!=='background');
  // Project exact node-bound controls into compact block sections, not synthetic recipes.
  const category = field => field.l.split(' · ')[0];
  const groups = [...new Set(fields.map(category))];
  return <div className="w-fields" data-manual-fields="bound">
    {groups.map(group => <section className="w-field-group w-block-fields" key={group}><div className="w-sec-h">{group}</div>{fields.filter(field => category(field) === group).map(field => <RW_BoundField key={b.id + ':' + field.k} blockId={b.id} field={field} setProp={setProp} assetNames={assetNames} assetContext={assetContext} />)}</section>)}
    {allFields.filter(field=>field.t==='background').map(field=><RW_BoundBackground key={b.id+':'+field.k} blockId={b.id} field={field} setProp={setProp} assetNames={assetNames} assetsBase={session?.artifacts?.assets_base} assetContext={assetContext}/>)}
  </div>;
}

function RW_CodeWell2({ b, editor, onRegen, onFull, canRebuild }) {
  const header = React.useRef(null);
  const [headerWidth, setHeaderWidth] = React.useState(500);
  React.useEffect(() => {
    if (!header.current) return;
    const observer = new ResizeObserver(entries => setHeaderWidth(entries[0].contentRect.width));
    observer.observe(header.current);
    return () => observer.disconnect();
  }, []);
  const draftBlock = editor.blocks.find(block => block.id === b.id);
  const value = draftBlock?.code ?? '';
  const dirty = editor.dirty;
  return (
    <div className="w-code">
      <div ref={header} className={'w-code-h' + (headerWidth < 390 ? ' tight' : '') + (headerWidth < 210 ? ' tighter' : '')}>
        <BI.code size={13} style={{ color: 'var(--muted)', flexShrink: 0 }} />
        <span className="w-code-f">{b.id}.block.html</span>
        <button className="w-chip" disabled={!canRebuild} onClick={() => onRegen(b.id)} title={canRebuild?'Rebuild this block from the field values':'Rebuild unavailable: exact HTML fields cannot safely regenerate custom markup.'}><BI.retry size={11} /> <span className="w-lbl">Rebuild from fields</span></button>
        {onFull && <button className="w-chip" style={{ padding: '5px 9px', fontSize: 11.5 }} onClick={onFull} title="Full-screen code editor" aria-label="Full-screen code editor"><BI.expand size={11} /></button>}
        {/* TE-106 (🐓 09-08): the label collapses under the Inspector's container query (`.w-code-h .w-lbl` → display:none ≤ 250px),
            which left an icon-only button with an EMPTY accessible name — a click aimed at the label's ref lands on a 0×0 element and
            nothing happens. The name lives on the button now; the hook is stable for e2e. */}
        {editor.canUndo && <button className="w-chip" onClick={editor.undo} title="Undo Code" aria-label="Undo Code"><BI.retry size={11}/><span className="w-lbl">Undo</span></button>}
        {dirty && <button className="w-chip" onClick={editor.discard} title="Revert Code" aria-label="Revert Code"><BI.x size={11}/><span className="w-lbl">Revert</span></button>}
        <button className="btn btn-primary btn-sm" disabled={!dirty || editor.stale} onClick={editor.apply} aria-label="Update draft" title="Update the local draft. Apply changes in the footer saves it and reruns QA." data-action="apply-code-draft"><BI.check size={13} /> <span className="w-lbl">Update draft</span></button>
      </div>
      {!draftBlock?.range ? <div role="alert">Section boundaries changed. Open Whole file to repair the retained Code draft.</div>
        : <CmBox value={value} onChange={value => editor.setBlock(b.id, value)} height={320} />}
    </div>
  );
}

const RW_ASSET_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,119}$/;

function rwAssetReferenceNames(html) {
  const names = new Set();
  const add = (raw) => {
    let value = String(raw || '').trim().replace(/^['"]|['"]$/g, '');
    if (!value || /^(?:[a-z][a-z0-9+.-]*:|\/\/|\/|#)/i.test(value)) return;
    value = value.replace(/&amp;/gi, '&').split(/[?#]/, 1)[0].replace(/\\/g, '/');
    let name = value.slice(value.lastIndexOf('/') + 1);
    try {name = decodeURIComponent(name);} catch (_) {}
    if (RW_ASSET_NAME_RE.test(name)) names.add(name);
  };
  const addSrcset = (value) => String(value || '').split(',').forEach((candidate) => add(candidate.trim().split(/\s+/, 1)[0]));
  try {
    const doc = new window.DOMParser().parseFromString('<body>' + String(html || '') + '</body>', 'text/html');
    doc.querySelectorAll('[src],[background],[poster],[srcset],[style]').forEach((node) => {
      for (const attribute of ['src', 'background', 'poster']) if (node.hasAttribute(attribute)) add(node.getAttribute(attribute));
      if (node.hasAttribute('srcset')) addSrcset(node.getAttribute('srcset'));
      if (node.hasAttribute('style')) {
        const style = node.getAttribute('style') || '';
        for (const match of style.matchAll(/url\(\s*(['"]?)(.*?)\1\s*\)/gi)) add(match[2]);
      }
    });
  } catch (_) {}
  // Conditional-comment markup is not part of the parsed DOM, but can still own an email asset.
  for (const match of String(html || '').matchAll(/(?:src|background|poster)\s*=\s*(['"])(.*?)\1/gi)) add(match[2]);
  for (const match of String(html || '').matchAll(/url\(\s*(['"]?)(.*?)\1\s*\)/gi)) add(match[2]);
  return names;
}

function rwAssetUrl(base, name) {
  if (!base || !name) return null;
  return String(base).replace(/\/?$/, '/') + encodeURIComponent(name);
}

function rwAssetBytes(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value < 0) return 'size unavailable';
  if (value < 1024) return value + ' B';
  if (value < 1024 * 1024) return (value / 1024).toFixed(value < 10 * 1024 ? 1 : 0) + ' KB';
  return (value / (1024 * 1024)).toFixed(1) + ' MB';
}

function rwAssetErrorDetail(error) {
  const data = error && error.data || {};
  const status = error && error.status;
  const code = data.code || error && error.code || (status === 413 ? 'too_large' : status === 415 ? 'not_an_image' : status === 400 ? 'bad_request' : null);
  const message = data.error || error && error.message || 'Failed to apply the asset edit.';
  if (code === 'revision_conflict') return { code, copy: 'the build moved — reload' };
  if (code === 'asset_exists') return { code, copy: message || 'That asset already exists — use Replace.' };
  if (code === 'asset_not_found') return { code, copy: 'That asset is no longer in the active revision — reload.' };
  if (code === 'base_drift') return { code, copy: 'The build base changed — reload before editing assets.' };
  if (code === 'base_not_terminal') return { code, copy: 'The build is still running — asset edits require a terminal build.' };
  if (code === 'revision_poisoned') return { code, copy: 'The revision graph failed its integrity check — reload and contact an administrator.' };
  if (code === 'too_large') return { code, copy: 'The asset is too large — ' + message };
  if (code === 'not_an_image') return { code, copy: 'The upload was refused as an image — ' + (data.reason || message) };
  if (code === 'extension_mismatch') return { code, copy: 'The file type does not match the asset name — sniffed ' + (data.sniffed || 'different image bytes') + '. Choose matching bytes; Replace keeps an existing name unchanged.' };
  if (code === 'format_not_client_safe') return { code, copy: 'The image is not client-safe for email — sniffed ' + (data.sniffed || 'an unsupported format') + '. ' + message };
  if (code === 'asset_name_invalid') return { code, copy: 'That asset name was refused — use the plain file name the build references (no paths).' };
  if (code === 'mode_invalid') return { code, copy: 'Unknown edit mode — reload the workspace and retry.' };
  if (code === 'cas_required') return { code, copy: 'The request did not name the revision it targets — reload to pick up the current head, then retry.' };
  if (code === 'file_required') return { code, copy: 'No file was attached — choose an image first.' };
  if (code === 'job_invalid') return { code, copy: 'The build id was refused — reopen the build from the Builds list.' };
  return { code: code || 'request_failed', copy: message };
}

function RW_AssetsSection({ b, job, session, draftDirty = false, bundleMutation, bundleBusy = false, onlyNames, inline = false, placementFields, sourceField }) {
  const replaceInput = React.useRef(null);
  const addInput = React.useRef(null);
  const replaceName = React.useRef(null);
  const [listing, setListing] = React.useState({ state: 'idle', data: null, error: null });
  const [busy, setBusy] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [failure, setFailure] = React.useState(null);
  const [offer, setOffer] = React.useState(null);
  const [showAll, setShowAll] = React.useState(false);
  const [overAsset, setOverAsset] = React.useState(null);
  const [renameDrafts,setRenameDrafts]=React.useState({});
  const [removeIntent,setRemoveIntent]=React.useState(null);
  const mounted = React.useRef(true);
  React.useEffect(() => { mounted.current = true;return () => {mounted.current = false;}; }, []);
  const api = window.GooseAPI;
  const jobId = job && job.id;
  const capable = !!(session && session.capabilities && session.capabilities.assets === true);
  const canReplace = !!(api && typeof api.replaceAsset === 'function');
  const canList = !!(api && typeof api.listAssets === 'function');
  const available = !!(jobId && capable && canReplace && canList && bundleMutation);
  const revision = session && session.revision;
  const revisionDigest = session && session.revision_digest;

  React.useEffect(() => {
    let alive = true;
    setFailure(null);
    setOffer(null);
    setRemoveIntent(null);
    setResult((current) => current && current.revision === revision ? current : null);
    if (!available) {
      setListing({ state: 'unavailable', data: null, error: null });
      return () => {alive = false;};
    }
    setListing((current) => ({ state: 'loading', data: current.data, error: null }));
    api.listAssets(jobId).then((data) => {
      if (!alive) return;
      if (data && revision && (data.revision !== revision || data.revision_digest !== revisionDigest)) {
        setListing({ state: 'error', data: null, error: 'the build moved — reload' });
        return;
      }
      setListing({ state: 'ready', data, error: null });
    }).catch((error) => {
      if (!alive) return;
      setListing({ state: 'error', data: null, error: rwAssetErrorDetail(error).copy });
    });
    return () => {alive = false;};
  }, [jobId, revision, revisionDigest, available, canList]);

  const submit = async (file, mode, name, newName) => {
    if ((!['remove','rename'].includes(mode) && !file) || busy || bundleBusy || !bundleMutation) return;
    if (draftDirty) { setFailure({ code: 'draft_pending', copy: 'Apply and verify or revert the HTML draft before changing assets.' });return; }
    if (typeof revision !== 'string' || !revision || typeof revisionDigest !== 'string' || !revisionDigest) {
      setFailure({ code: 'missing_cas', copy: 'The current revision head is unavailable — reload.' });
      return;
    }
    setBusy({ mode, name });
    setFailure(null);
    setResult(null);
    setOffer(null);
    try {
      const response = await bundleMutation('asset_' + mode, () => api.replaceAsset(jobId, {
        file,
        mode,
        name,
        newName,
        blockId: b && b.id || undefined,
        expected_head: revision,
        expected_head_digest: revisionDigest,
      }));
      if (!response || !mounted.current) return;
      setResult({
        copy: name + ' ' + (mode === 'replace' ? 'replaced' : mode === 'remove' ? 'removed' : mode==='rename'?'renamed to '+newName:'added') + ' — rerun QA to re-verify',
        revision: response && response.revision && response.revision.revision,
      });
    } catch (error) {
      if (!mounted.current) return;
      const detail = rwAssetErrorDetail(error);
      setFailure(detail);
      if (detail.code === 'asset_exists') setOffer({ file, name });
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  if (!available) {
    return <section data-assets-state="unavailable" style={{ flexShrink: 0 }}>
      <div className="w-sec-h">Assets</div>
      <div className="w-callout info"><BI.image size={15} /><span>Asset editing is unavailable for this revision (RWB-5).</span></div>
    </section>;
  }

  const references = rwAssetReferenceNames(b && b.code);
  const allAssets = listing.data && Array.isArray(listing.data.assets) ? listing.data.assets : [];
  const assets = allAssets.filter((asset) => asset && (onlyNames ? onlyNames.includes(asset.name) : showAll || references.has(asset.name)));
  const assetsBase = session && session.artifacts && session.artifacts.assets_base;
  return (
    <section className={inline?'w-inline-asset':''} data-assets-state={listing.state} style={{ flexShrink: 0 }}>
      <div className="w-sec-h">{inline?'Image file':'Assets'}<span className="w-grow"></span>{!inline&&<span>{listing.state === 'ready' ? assets.length : '—'}</span>}
        {!inline&&<button className="w-chip" data-asset-action="scope" onClick={() => setShowAll(value => !value)}>{showAll ? 'This block' : 'All bundle assets'}</button>}
        <button className="w-chip" data-asset-action="add" disabled={!!busy || bundleBusy || draftDirty} onClick={() => addInput.current && addInput.current.click()}><BI.add size={11} /> Add</button>
      </div>
      {listing.state === 'loading' && <div className="w-bar-note">Loading assets…</div>}
      {draftDirty && <div className="w-bar-note">Apply and verify or revert the HTML draft before changing assets.</div>}
      {listing.state === 'error' && <div className="w-callout minor" role="alert" data-asset-result="error"><BI.warn size={14} /><span>{listing.error}</span></div>}
      {listing.state === 'ready' && assets.map((asset) => {
        const dimensions = asset.w && asset.h ? asset.w + '×' + asset.h : 'dimensions unavailable';
        const motion = typeof asset.animated !== 'boolean'
          ? 'animation unavailable'
          : asset.animated ? 'animated · ' + (asset.frames == null ? 'frames unavailable' : asset.frames + ' frames') : 'static';
        return <div className="w-asset" data-asset-name={asset.name} key={asset.name} style={{ padding: 12, marginBottom: 10, borderRadius: 13, background: 'var(--surface-raised)', boxShadow: 'var(--nm-out-sm)', flexShrink: 0 }}>
          <button type="button" className={'w-asset-slot has' + (overAsset === asset.name ? ' over' : '')} aria-label={'Replace ' + asset.name} disabled={!!busy || bundleBusy || draftDirty}
            onClick={() => {replaceName.current = asset.name;replaceInput.current?.click();}}
            onDragOver={e => {e.preventDefault();if (!busy && !bundleBusy && !draftDirty) setOverAsset(asset.name);}}
            onDragLeave={() => setOverAsset(null)} onDrop={e => {e.preventDefault();setOverAsset(null);const files=[...e.dataTransfer.files];if(files.length!==1){setFailure({code:'file_required',copy:'Drop exactly one replacement image.'});return;}submit(files[0],'replace',asset.name);}}>
            {assetsBase && <img className="w-asset-img" src={rwAssetUrl(assetsBase, asset.name)} alt={asset.name} />}
            <span className="w-asset-overlay"><BI.image size={18} /> Drop an image or click to replace</span>
          </button>
          <div className="w-asset-row" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <span style={{ flex: '1 1 120px', minWidth: 0 }}><b style={{ display: 'block', fontSize: 12.5, overflowWrap: 'anywhere' }}>{asset.name}</b><span className="w-asset-meta" style={{ whiteSpace: 'normal' }}>{dimensions} · {rwAssetBytes(asset.bytes)} · {motion}</span></span>
            <button className="w-chip" data-asset-action="replace" data-asset-target={asset.name} disabled={!!busy || bundleBusy || draftDirty} onClick={() => {replaceName.current = asset.name;if (replaceInput.current) replaceInput.current.click();}}>Replace</button>
            {(showAll||inline) && <button className="w-chip" data-asset-action="remove" data-asset-target={asset.name} disabled={!!busy || bundleBusy || draftDirty} aria-expanded={removeIntent===asset.name} onClick={() => setRemoveIntent(asset.name)}>Remove</button>}
          </div>
          {removeIntent===asset.name && <div className="w-callout minor" role="group" aria-label={'Confirm removal of '+asset.name}><span>Remove this file from the bundle? Referenced files are refused; previous revisions remain restorable.</span><button type="button" className="btn btn-danger btn-sm" disabled={!!busy||bundleBusy||draftDirty} data-asset-confirm-remove onClick={()=>{setRemoveIntent(null);submit(null,'remove',asset.name);}}>Remove file</button><button type="button" className="btn btn-quiet btn-sm" onClick={()=>setRemoveIntent(null)}>Cancel</button></div>}
          <div className="w-fhint">Replaces this file everywhere it is used in the bundle. Previous versions, including GIF animation, remain restorable.</div>
          {inline&&<div className="w-row"><input className="w-in" aria-label={'Filename for '+asset.name} value={renameDrafts[asset.name]??asset.name} disabled={!!busy||bundleBusy||draftDirty} onChange={e=>setRenameDrafts({...renameDrafts,[asset.name]:e.target.value})}/><button className="w-chip" disabled={!!busy||bundleBusy||draftDirty||!renameDrafts[asset.name]||renameDrafts[asset.name]===asset.name} onClick={()=>submit(null,'rename',asset.name,renameDrafts[asset.name])}>Rename</button></div>}
          {inline&&<div className="w-fhint">Rename updates supported HTML, CSS and VML paths atomically. Unusual references are refused. To remove a used image, detach its reference in Code first.</div>}
          {sourceField&&Object.values(placementFields||{}).filter(f=>f.k.startsWith(sourceField.k.replace(/_src$/,'')+'_')&&/_width(?:_attribute)?$/.test(f.k)&&f.u!=='%').some(f=>Number(f.value)>Number(asset.w)&&asset.w>0)&&<div className="w-callout minor" role="status">This placement enlarges the source image. Re-export at 2× the intended width for sharper results. GIF replacement preserves the uploaded animation bytes.</div>}
        </div>;
      })}
      {listing.state === 'ready' && !assets.length && <div className="w-bar-note" data-assets-empty="1">No manifest assets are referenced by this block.</div>}
      {busy && <div className="w-bar-note" data-asset-progress={busy.mode} role="status"><progress aria-label={(busy.mode === 'replace' ? 'Replacing ' : busy.mode === 'remove' ? 'Removing ' : busy.mode==='rename'?'Renaming ':'Adding ') + busy.name}></progress> {busy.mode === 'replace' ? 'Replacing ' : busy.mode === 'remove' ? 'Removing ' : busy.mode==='rename'?'Renaming ':'Adding '}{busy.name}…</div>}
      {failure && <div className="w-callout minor" role="alert" data-asset-result="error" data-asset-error-code={failure.code} style={{ marginTop: 10 }}><BI.warn size={14} /><span>{failure.copy}{offer && <React.Fragment><br /><button data-asset-offer="replace" disabled={!!busy} onClick={() => submit(offer.file, 'replace', offer.name)}>Replace existing</button></React.Fragment>}</span></div>}
      {result && <div className="w-callout pass" role="status" data-asset-result="success" style={{ marginTop: 10 }}><BI.check size={14} /><span>{result.copy}</span></div>}
      <input ref={replaceInput} type="file" accept="image/*" hidden onChange={(event) => {const file = event.target.files && event.target.files[0];const name = replaceName.current;event.target.value = '';if (file && name) submit(file, 'replace', name);}} />
      <input ref={addInput} type="file" accept="image/*" hidden onChange={(event) => {const file = event.target.files && event.target.files[0];event.target.value = '';if (file) submit(file, 'add', file.name);}} />
    </section>
  );
}

const RW_FINDING_COLORS = {
  pass: 'var(--pass)',
  minor: 'var(--cond)',
  major: 'var(--fail)',
  neutral: 'var(--faint)',
};
const RW_CHASE_GATE = 'New Chase requests are unavailable. Retained results remain reviewable.';
let RW_ACTIVE_CHASE = null;

function RW_FindingsList({ B, chase }) {
  const blocks = Array.isArray(B.blocks) ? B.blocks : [];
  const unplaced = Array.isArray(B.unplaced) ? B.unplaced : [];
  const handToChase = (block) => {
    if (!chase || typeof chase.ask !== 'function') return;
    if (typeof chase.setScope === 'function') chase.setScope('region');
    chase.ask('Address this review finding in ' + block.label + ': ' + (block.note || 'No finding detail was materialized.'), { scope: 'region', blockId: block.id });
  };
  return (
    <div>
      <div className="w-sec-h">Per-section findings<span className="w-grow"></span><span>{blocks.length} region{blocks.length === 1 ? '' : 's'}</span></div>
      {blocks.map((block) => {
        const qa = block.qa || 'neutral';
        const note = block.note || 'No finding names this section';
        return (
          <div key={block.id} className={'w-find' + (block.id === B.sel ? ' sel' : '')} onClick={() => B.setSel(block.id)}>
            {block.qa
              ? <span className={'w-ft ' + block.qa}>{block.qa}</span>
              : <span className="dot" style={{ width: 9, height: 9, borderRadius: 5, marginTop: 5, flexShrink: 0, background: RW_FINDING_COLORS[qa] }} title="No named finding"></span>}
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="w-find-n" style={{ display: 'block' }}>{block.label}</span>
              <span className="w-find-d" style={{ display: 'block' }}>{note}</span>
              {B.touched && B.touched.has(block.id) && <span className="w-rerun">edited here · QA re-runs on apply</span>}
            </span>
            {block.qa && block.qa !== 'pass' && typeof chase?.ask === 'function' && (
              <button className="w-cm-act strong" disabled={!!(chase && chase.busy)} onClick={(e) => { e.stopPropagation(); handToChase(block); }}><BI.jet size={11} /> hand to Chase</button>
            )}
          </div>
        );
      })}
      {!blocks.length && <div className="w-find-none">No editable regions materialized for this lane.</div>}
      {!!unplaced.length && (
        <div style={{ marginTop: 16 }}>
          <div className="w-sec-h">Unplaced findings<span className="w-grow"></span><span>{unplaced.length}</span></div>
          {unplaced.map((finding, index) => (
            <div className="w-find" key={(finding.label || finding.section || 'finding') + index}>
              <span className="dot" style={{ width: 9, height: 9, borderRadius: 5, marginTop: 5, flexShrink: 0, background: RW_FINDING_COLORS[finding.tag] || RW_FINDING_COLORS.neutral }}></span>
              <span style={{ flex: 1, minWidth: 0 }}><span className="w-find-n" style={{ display: 'block' }}>{finding.label || finding.section || 'Build-level finding'}</span><span className="w-find-d" style={{ display: 'block' }}>{finding.note || 'No finding detail was materialized.'}</span></span>
            </div>
          ))}
        </div>
      )}
      {chase && !chase.wired && <div className="w-callout info" style={{ marginTop: 14 }}><BI.jet size={15} /><span>{chase.availabilityMessage || RW_CHASE_GATE}</span></div>}
    </div>
  );
}

function InspectorTab({ B, chase, onViewDiff, onFull, job, session, canAmend = true }) {
  RW_ACTIVE_CHASE = chase || RW_ACTIVE_CHASE;
  const [mode, setMode] = React.useState('manual');
  const b = B.cur;
  if (!b) {
    return <div className="w-scroll w-pad"><div className="w-callout info"><BI.warn size={15} /><span>No editable regions materialized for this lane.</span></div></div>;
  }
  const hasFields = Object.values(b.fields || {}).some(field => field?.schema === 'ArtifactField.v1');
  const activeMode = hasFields ? mode : 'code';
  const patch = (chase.queue || []).find((proposal) => proposal.blockId === b.id);
  const patchId = patch && (patch.pid == null ? (patch.id == null ? patch.blockId : patch.id) : patch.pid);
  const identity = b.identity || B.identity || 'derived';
  const assetContext={b,job,session,bundleMutation:B.bundleMutation,bundleBusy:B.bundleBusy,draftDirty:!!B.editor?.dirty||!!B.hist?.length&&B.hist.at(-1).snap!==B.hist[0].snap};
  return (
    <React.Fragment>
      <div className="w-sub">
        <h3>{b.label}</h3>
        <code data-inspector-anchor={b.id} title={identity === 'authored' ? 'Authored region identity' : 'Derived display anchor — not authored authority'}>{identity === 'authored' ? 'data-region' : 'data-sec'}="{b.id}"</code>
      </div>
      <div className="w-mode-row">
        {canAmend ? <div className="w-seg">
          <button className={activeMode === 'manual' ? 'on' : ''} disabled={!hasFields} title={!hasFields ? b.manual_unavailable || 'No safely bound Manual fields. Use Code for this block.' : undefined} onClick={() => setMode('manual')}><BI.sliders size={13} /> Manual</button>
          <button className={activeMode === 'code' ? 'on' : ''} onClick={() => setMode('code')}><BI.code size={13} /> Code</button>
        </div> : <span className="w-meta">HTML amendments unavailable</span>}
        <span className="w-grow" style={{ flex: 1 }}></span>
        <span className="w-meta">block {B.blocks.indexOf(b) + 1} / {B.blocks.length}</span>
      </div>
      <div className="w-scroll w-pad" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {canAmend && patch && (
          <div className="w-callout pass">
            <BI.jet size={15} />
            <span>
              <b>Chase has a proposal staged here.</b><br />{patch.reason || patch.why || 'Review the proposed source change before accepting it.'}
              <span style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                {chase.canAccept
                  ? <button className="btn btn-primary btn-sm" disabled={!!chase.phase || chase.disabled || chase.acceptBlocked || patch.stale} onClick={() => chase.accept(patchId)} data-action="chase-accept"><BI.check size={12} /> {chaseAcceptLabel(chase.queue.filter(item => item.pid === patchId))}</button>
                  : <span className="muted" style={{ fontSize: 12 }} data-chase-accept="unavailable">Acceptance is unavailable for this revision.</span>}
                <button className="btn btn-quiet btn-sm" onClick={() => chase.discard(patchId)}>Discard</button>
                <button className="btn btn-quiet btn-sm" onClick={() => onViewDiff && onViewDiff(patch.blockId)}><BI.code size={12} /> View diff</button>
              </span>
            </span>
          </div>
        )}
        {!canAmend && <div className="w-callout info" style={{ flexShrink: 0 }}><BI.warn size={15} /><span>HTML amendments are unavailable for this revision. Asset access is governed separately below.</span></div>}
        {canAmend && activeMode === 'manual' && B.editor?.dirty && <div className="w-callout info" data-code-draft-pending="1"><span>Code edits are retained. Update the local draft or revert them before Manual editing. Apply changes in the footer saves the draft and reruns QA; neither action approves the email.</span><button onClick={B.editor.apply} disabled={B.editor.stale}>Update draft</button><button onClick={B.editor.discard}>Revert Code</button></div>}
        {(!hasFields || !canAmend) && <RW_AssetsSection key={(job && job.id || 'job') + ':' + b.id} {...assetContext} />}
        {canAmend && activeMode === 'manual' && !B.editor?.dirty && <RW_Fields2 b={b} setProp={B.setProp} setProps={B.setProps} job={job} session={session} assetContext={assetContext} />}
        {canAmend && activeMode === 'code' && (
          <RW_CodeWell2 key={b.id}   // RW-01e integration (🐓): fresh well state per block — no stale-textarea frame on selection change
            b={b}
            editor={B.editor}
            onRegen={B.regen}
            onFull={onFull}
            canRebuild={false}
          />
        )}
        {canAmend && activeMode === 'manual' && b.custom && (
          <div className="w-callout info"><BI.warn size={15} /><span><b>Hand-edited in code.</b> Manual edits never regenerate this section or discard unrelated code. <button onClick={() => setMode('code')}>View code</button></span></div>
        )}
        <div className={'w-callout ' + ({pass:'pass',minor:'minor',major:'fail'}[b.qa] || 'info')} data-inspector-finding={b.id}>
          {b.qa === 'pass' ? <BI.check size={15} /> : b.qa === 'minor' || b.qa === 'major' ? <BI.warn size={15} /> : <BI.minus size={15} />}
          <span><b>Review finding.</b> {b.note || 'No finding names this region.'}</span>
        </div>
      </div>
    </React.Fragment>
  );
}

const RW_RECEIPT_STATES = {
  candidate_ready: { label: 'Candidate ready', className: 'pass', icon: 'check' },
  needs_input: { label: 'Needs input', className: 'minor', icon: 'warn' },
  qa_exhausted: { label: 'QA exhausted', className: 'info', icon: 'minus' },
};

function ReviewTab({ job, c, isLive, B, chase, qaSummary, imgRefAudit, textAccuracy, afterburnerStatus, receipt, pre, setPre,
  notesOpen, notes, setNotes, onRunAfterburner, unplaced, revisionQa }) {
  const reviewJob = job || {};
  const actions = !isLive && Array.isArray(pre) ? pre : [];
  const done = actions.filter((action) => action.done).length;
  const verdict = reviewJob.verdict || (isLive ? 'UNMEASURED' : 'CONDITIONAL');
  const mockVerdict = '“Build is production-grade. Two minor items want confirmation: rate-table legibility at 125% zoom, and the SBS disclaimer color (#343741 vs body #3B3C43). No broken references.”';
  const verdictCopy = isLive
    ? (qaSummary && qaSummary.summary ? qaSummary.summary : verdict + ' · full report not materialized for this lane')
    : mockVerdict;

  let brokenRefs;
  let imageFallbacks;
  let textCard;
  if (!isLive) {
    brokenRefs = { value: 'None', tone: 'pass' };
    imageFallbacks = { value: 'None', tone: 'pass' };
    textCard = { value: 'PASS', tone: 'pass' };
  } else if (imgRefAudit) {
    const missing = Array.isArray(imgRefAudit.missing) ? imgRefAudit.missing.length : null;
    const fallbackRows = imgRefAudit.forbidden_fallbacks ?? imgRefAudit.fallbacks_used;
    const fallbacks = Array.isArray(fallbackRows) ? fallbackRows.length : null;
    brokenRefs = { value: missing === null ? 'Unmeasured' : missing === 0 ? 'None' : missing + ' missing', tone: missing === null ? null : missing === 0 ? 'pass' : 'cond' };
    imageFallbacks = { value: fallbacks === null ? 'Unmeasured' : fallbacks === 0 ? 'None' : fallbacks + ' used', tone: fallbacks === null ? null : fallbacks === 0 ? 'pass' : 'cond' };
  } else {
    brokenRefs = { value: 'Not materialized for this lane', tone: null };
    imageFallbacks = { value: 'Not materialized for this lane', tone: null };
  }
  if (isLive) {
    if (typeof textAccuracy?.accuracy_percent === 'number' && Number.isFinite(textAccuracy.accuracy_percent)) {
      textCard = { value: textAccuracy.accuracy_percent.toFixed(1) + '%', tone: 'neutral' };
    } else {
      textCard = { value: 'Not materialized for this lane', tone: null };
    }
  }
  const afterburner = isLive && afterburnerStatus === undefined
    ? { value: 'Checking Afterburner status…', tone: null, status: 'loading' }   // RW-01d integration (🐓): shell passes undefined only while the fetch is in flight
    : isLive && !afterburnerStatus
      ? { value: 'Not materialized for this lane', tone: null, status: 'not_materialized' }
      : reviewAfterburnerCard(afterburnerStatus || { status: 'not_started' });
  const viewer = window.RBAC && window.RBAC.viewer && window.RBAC.viewer();
  const canRunAfterburner = viewer
    ? !!(window.RBAC.requireRole && window.RBAC.requireRole('afterburner'))
    : true;
  const showRunAfterburner = (afterburner.status === 'not_started' || afterburner.status === 'idle') && canRunAfterburner;
  const receiptState = receipt && (RW_RECEIPT_STATES[receipt.status] || { label: receipt.status || 'Run receipt', className: 'info', icon: 'minus' });
  const ReceiptIcon = receiptState && BI[receiptState.icon];
  const renderValue = (card) => card.tone
    ? <span className={card.tone === 'neutral' ? 'chip' : 'pill pill-' + card.tone} style={{ fontSize: 11 }}>{card.tone === 'pass' || card.tone === 'run' ? <span className="dot"></span> : card.tone === 'neutral' ? null : <I.warn size={10} />}{card.value}</span>
    : <span className="muted" style={{ fontSize: 11 }}>{card.value}</span>;

  return (
    <div className="w-scroll">
      <div className="w-stack">
        {isLive && window.RwRevisionQaCard && <window.RwRevisionQaCard evidence={revisionQa} />}
        {isLive && <div className="w-sec-h" data-original-evidence="1">Original build report · historical, not current revision QA</div>}
        <div className={'verdict-banner ' + (verdict === 'PASS' ? 'pass' : verdict === 'FAIL' ? 'fail' : 'cond')} data-review-verdict={verdict}>
          <span className="vb-ico" style={{ color: verdict === 'PASS' ? 'var(--pass)' : verdict === 'FAIL' ? 'var(--fail)' : 'var(--cond)' }}><I.warn size={22} /></span>
          <div className="grow">
            <div style={{ fontSize: 16, fontWeight: 800, color: verdict === 'PASS' ? 'var(--pass)' : verdict === 'FAIL' ? 'var(--fail)' : 'var(--cond)' }}>{verdict}</div>
            <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>{verdictCopy}</div>
          </div>
          {textAccuracy && textAccuracy.accuracy_percent != null && <span className="chip mono">text-accuracy {Number(textAccuracy.accuracy_percent).toFixed(1)}%</span>}
        </div>

        {receipt && (
          <div className={'w-callout ' + receiptState.className} data-review-receipt={receipt.status || 'unknown'}>
            <ReceiptIcon size={15} />
            <span><b>{receiptState.label}</b> · run receipt{receipt.repair_count != null ? ' · ' + receipt.repair_count + ' repair' + (receipt.repair_count === 1 ? '' : 's') : ''}</span>
          </div>
        )}

        <div data-review-qa={isLive ? 'live' : 'mock'}>
          <div className="w-sec-h">
            {isLive ? 'Original build measurements' : 'Demo QA'}
            <span className="w-grow"></span>
            {isLive && <span className="w-tag patch">historical</span>}
            {!!(B.touched && B.touched.size) && <span className="chip">Draft changes are not covered by these measurements</span>}
          </div>
          <div className="w-qa">
            <div className="w-qa-c" data-qa-card="Broken refs">
              <div className="w-qa-k">Broken refs</div>
              <div className="w-qa-v">{renderValue(brokenRefs)}</div>
            </div>
            <div className="w-qa-c" data-qa-card="Image fallbacks">
              <div className="w-qa-k">Image fallbacks</div>
              <div className="w-qa-v">{renderValue(imageFallbacks)}</div>
            </div>
            <div className="w-qa-c" data-qa-card="Text accuracy">
              <div className="w-qa-k">Text accuracy</div>
              <div className="w-qa-v">{renderValue(textCard)}</div>
            </div>
            <div className="w-qa-c" data-qa-card="Email on Acid">
              <div className="w-qa-k">Email on Acid</div>
              <div className="w-qa-v">
                {afterburner.tone
                  ? <span className={afterburner.tone === 'neutral' ? 'chip' : 'pill pill-' + afterburner.tone} style={{ fontSize: 11 }} data-afterburner-status={afterburner.status}>{afterburner.tone === 'pass' || afterburner.tone === 'run' ? <span className="dot"></span> : afterburner.tone === 'neutral' ? null : <I.warn size={10} />}{afterburner.value}</span>
                  : <span className="muted" style={{ fontSize: 11 }} data-afterburner-status={afterburner.status}>{afterburner.value}</span>}
                {showRunAfterburner && <button type="button" className="btn btn-quiet" style={{ padding: '4px 7px', fontSize: 10.5 }} onClick={onRunAfterburner} data-afterburner-run="1">Run Afterburner</button>}
              </div>
            </div>
          </div>
          {isLive && (
            <p className="muted" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.5 }}>
              Open the job artifacts for the full <span className="mono">qa-report.md</span> / <span className="mono">image-ref-audit.json</span> / <span className="mono">text-accuracy-audit.json</span>.
            </p>
          )}
        </div>

        {isLive && <p className="w-bar-note">No pre-send checklist is recorded for this build.</p>}
        {!isLive && <div>
          <div className="w-sec-h">Pre-send actions · demo data<span className="w-grow"></span><span>{done}/{actions.length}</span></div>
          {actions.map((action) => (
            <div key={action.id} className="check-item" onClick={() => setPre((items) => items.map((item) => item.id === action.id ? { ...item, done: !item.done } : item))} style={{ cursor: 'pointer' }}>
              <span className={'check-box' + (action.done ? ' checked' : '')}>{action.done && <I.check size={13} />}</span>
              <span style={{ fontSize: 12.5, color: action.done ? 'var(--muted)' : 'var(--ink)', textDecoration: action.done ? 'line-through' : 'none' }}>{action.label}</span>
            </div>
          ))}
        </div>}

        {notesOpen && (
          <div>
            <label className="field-lbl">Change request notes</label>
            <textarea className="input" rows={3} placeholder="e.g. increase rate-table font to 14px, confirm disclaimer #343741…" value={notes} onChange={(e) => setNotes(e.target.value)}></textarea>
            <p className="muted" style={{ fontSize: 11, marginTop: 6 }}>Notes stay with this in-session review until the durable revision workflow lands.</p>
          </div>
        )}

        <RW_FindingsList B={{...B, unplaced: unplaced || B?.unplaced}} chase={chase} />
      </div>
    </div>
  );
}

const RW_CHASE_SCOPES = [
  { key: 'region', label: 'This block' },
  { key: 'document', label: 'Whole file', title: 'Explicitly request document scope' },
];

function RwChaseProposalDialog({ proposal, B, chase, job, onClose }) {
  const [region, setRegion] = React.useState(proposal.patches?.[0]?.region_id || '');
  const [view, setView] = React.useState('diff');
  const [base, setBase] = React.useState({ status: 'loading' });
  const jobId = job?.id || job?._id;
  React.useEffect(() => {
    let current = true;
    setBase({ status: 'loading' });
    (async () => {
      try {
        if (!jobId || !window.GooseAPI?.readRevision || !proposal.base?.revision) throw Error('The original revision is unavailable. Proposed code remains readable.');
        const envelope = await window.GooseAPI.readRevision(jobId, proposal.base.revision);
        if (envelope.revision !== proposal.base.revision || envelope.revision_digest !== proposal.base.revision_digest) throw Error('The original revision does not match this proposal.');
        const html = await rwFetchRevisionHtml(rwRevisionHtmlUrl(envelope));
        const blocks = window.GooseReviewBlocks?.blocksFromHtml(html)?.blocks || [];
        if (current) setBase({ status: 'ready', html, blocks });
      } catch (error) { if (current) setBase({ status: 'error', error: error.message || 'Could not read the original revision.' }); }
    })();
    return () => {current = false;};
  }, [jobId, proposal.proposal_id, proposal.base?.revision, proposal.base?.revision_digest]);
  const patch = proposal.patches?.find(item => item.region_id === region) || proposal.patches?.[0];
  const label = id => id === 'document' ? 'Whole email' : B.blocks?.find(block => block.id === id)?.label || id;
  const before = base.status === 'ready' && patch
    ? patch.region_id === 'document' ? base.html : base.blocks.find(block => block.id === patch.region_id)?.code : null;
  const diff = typeof before === 'string' ? rwRevisionDiffExcerpt(before, patch.after_code || '', 500) : null;
  const staged = proposal.status === 'proposed';
  const stale = !chase.queue?.some(item => item.pid === proposal.proposal_id) || chase.queue.some(item => item.pid === proposal.proposal_id && item.stale);
  const busy = !!chase.phase, blocked = busy || chase.disabled || chase.acceptBlocked || stale;
  return <RwHistoryDialog label={staged ? 'Review Chase proposal' : 'Retained Chase proposal'} onClose={onClose} busy={busy}
    className="w-fw w-chase-proposal-dialog" data-chase-proposal-dialog={proposal.proposal_id}>
    <header className="w-fw-h"><span className="w-spark"><BI.jet size={15} /></span><div className="w-grow"><b>{staged ? 'Review staged proposal' : 'Proposal history'}</b><span>{proposal.status} · Original {proposal.base?.revision || 'revision unavailable'} · {proposal.patches?.length || 0} {proposal.patches?.length === 1 ? 'patch' : 'patches'}</span></div>
      <button className="btn btn-quiet btn-sm" disabled={busy} aria-label="Close proposal" onClick={onClose}><BI.x size={15} /></button></header>
    <div className="w-fw-b">
      <p className="w-chase-dialog-explain">{proposal.explain || 'No explanation was recorded.'}</p>
      <details className="w-chase-details"><summary>Request details</summary><p className="w-meta">{proposal.proposal_id} · Original {proposal.base?.revision} · {proposal.base?.revision_digest}</p></details>
      {!staged && <p className="w-bar-note">Read-only history. This proposal cannot be accepted again and does not grant approval.</p>}
      {staged && stale && <p role="alert" className="w-bar-note">This proposal is stale. Reload or request a new proposal before accepting.</p>}
      {patch && <React.Fragment>
        <label className="w-lbl">Proposed section<select className="w-in" value={patch.region_id} onChange={event => setRegion(event.target.value)} aria-label="Proposed section">
          {proposal.patches.map(item => <option value={item.region_id} key={item.region_id}>{label(item.region_id)}</option>)}</select></label>
        <div className="w-chase-dialog-views" role="group" aria-label="Proposal code view">{[['diff','Changes'],['before','Original'],['after','Proposed']].map(([id,text]) => <button className={'w-chip' + (view === id ? ' on' : '')} aria-pressed={view === id} key={id} onClick={() => setView(id)}>{text}</button>)}</div>
        <p className="w-bar-note">{patch.reason}</p>
        {view !== 'after' && base.status === 'loading' && <p role="status">Loading the exact original revision…</p>}
        {view !== 'after' && base.status === 'error' && <p role="alert">{base.error} Open Proposed to inspect the retained code.</p>}
        {view !== 'after' && base.status === 'ready' && before == null && <p role="alert">This section is absent from the original revision. No comparison has been invented.</p>}
        {view === 'diff' && diff && <React.Fragment><div className="w-chase-dialog-diff">{diff.lines.map((line,index) => <div className={'w-dl '+line.t} key={index}><i>{line.t === 'add' ? '+' : line.t === 'del' ? '−' : ''}</i>{line.s}</div>)}</div><p className="w-bar-note">− Original · + Proposed{diff.truncated ? ' · Excerpt limited to 500 lines; Original and Proposed show the full code.' : ''}</p></React.Fragment>}
        {(view === 'after' || view === 'before' && typeof before === 'string') && <pre className="w-chase-dialog-code">{view === 'after' ? patch.after_code : before}</pre>}
      </React.Fragment>}
      {!patch && <p>No code patches were retained.</p>}
      {chase.error && <div role="alert" className="w-callout info"><span>{chase.error.title}{chase.error.detail ? ' · '+chase.error.detail : ''}</span></div>}
    </div>
    <footer className="w-fw-f"><span className="w-bar-note w-grow">{staged ? 'Accept saves one revision and runs full-email QA. It does not approve the email.' : 'The saved email is unchanged.'}</span>
      {staged && <button className="btn btn-quiet btn-sm" disabled={busy || chase.disabled} onClick={() => chase.discard(proposal.proposal_id)}>Discard</button>}
      {staged && chase.canAccept && <button className="btn btn-primary btn-sm" disabled={blocked} onClick={() => chase.accept(proposal.proposal_id)}>{chaseAcceptLabel(proposal.patches)}</button>}
    </footer>
  </RwHistoryDialog>;
}

function ChaseTab({ B, chase, onViewDiff, job }) {
  RW_ACTIVE_CHASE = chase || RW_ACTIVE_CHASE;
  const [value, setValue] = React.useState('');
  const [intent, setIntent] = React.useState('edit');
  const [minorPreset, setMinorPreset] = React.useState(false);
  const [openProposalId, setOpenProposalId] = React.useState(null);
  const scroller = React.useRef(null);
  React.useEffect(() => {
    if (chase.composerDraft) {setValue(chase.composerDraft.text || '');setIntent('edit');setMinorPreset(false);}
  }, [chase.composerDraft]);
  React.useEffect(() => {
    const el = scroller.current;
    const question = [...(el?.querySelectorAll('[data-chase-question]') || [])].at(-1);
    const staged = [...(el?.querySelectorAll('[data-chase-proposal]') || [])].at(-1);
    if (el) el.scrollTo({ top: staged ? staged.getBoundingClientRect().bottom - el.getBoundingClientRect().bottom + el.scrollTop
      : question ? question.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop : 0, behavior: 'smooth' });
  }, [chase.msgs?.length,chase.operations?.length,chase.proposals?.length]);
  React.useEffect(() => {
    if(!chase.retainedProposalId)return;
    if(chase.retainedProposals?.some(proposal => proposal.proposal_id === chase.retainedProposalId)) setOpenProposalId(chase.retainedProposalId);
  },[chase.retainedProposalId,chase.retainedProposals]);
  const regionAvailable = !!(B.cur && B.cur.identity === 'authored');
  const validScope = chase.scope === 'multi' ? 'multi' : !regionAvailable || chase.scope === 'document' ? 'document' : 'region';
  const selectionInvalid = validScope === 'multi' && !chase.multiValid;
  const scopeNeedsConfirmation = intent !== 'explain' && validScope === 'document' && !chase.documentScopeExplicit;
  const minors = chaseMinorScope(B.blocks);
  const minorSelection = React.useRef(null);
  const minorKey = (minors.regionIds || []).join('|');
  const minorPresetActive = minorPreset && minors.enabled && validScope === minors.scope
    && (minors.scope === 'region' ? B.cur?.id === minors.regionIds[0]
      : minors.regionIds.length === chase.regionIds?.length && minors.regionIds.every(id => chase.regionIds.includes(id)));
  React.useEffect(() => {
    if (minorPreset && minorSelection.current !== minorKey) {
      setMinorPreset(false); chase.setRegionIds([]); chase.setScope('region');
    }
  }, [minorPreset, minorKey]);
  const go = (text = value, clearDraft = true) => {
    const instruction = text.trim();
    if (!instruction || minorPreset && !minorPresetActive || chase.busy || chase.disabled || chase.acceptBlocked || !chase.wired || selectionInvalid || scopeNeedsConfirmation) return;
    chase.ask(instruction, { intent });
    if (clearDraft) {setValue('');chase.clearComposerDraft?.();}
  };
  const quickAsk = prompt => {
    if (scopeNeedsConfirmation) {
      setValue(draft => draft.trim() ? `${draft}\n${prompt}` : prompt);
      return;
    }
    go(prompt, false);
  };
  const proposals = chase.proposals || [];
  const stagedPatches = proposals.flatMap(proposal => (proposal.patches || []).map(patch => ({ proposal, patch,
    queued: (chase.queue || []).find(item => item.pid === proposal.proposal_id && item.blockId === patch.region_id),
  })));
  const allProposals = [...proposals, ...(chase.retainedProposals || [])].filter(proposal => !proposal.member_proposal_ids);
  const openProposal = (chase.retainedProposals || []).find(proposal => proposal.proposal_id === openProposalId);
  const closeProposal = () => {setOpenProposalId(null);chase.viewRetainedProposal?.(null);};
  const operations = chase.operations || [];
  const turns = operations.map(op => ({ key: op.operation_id, op,
    proposal: allProposals.find(p => p.op_id === op.operation_id || p.proposal_id === op.proposal_id),
    instruction: op.instruction, created_at: op.created_at }));
  allProposals.filter(p => !turns.some(turn => turn.proposal === p)).forEach(proposal => turns.push({
    key: proposal.op_id || proposal.proposal_id, proposal, instruction: proposal.instruction, created_at: proposal.created_at,
  }));
  (chase.msgs || []).filter(msg => !msg.operation_id).forEach((msg, index) => turns.push({
    key: 'local-' + (msg.id ?? index), msg, created_at: msg.created_at,
  }));
  turns.sort((a, b) => (Date.parse(a.created_at) || 0) - (Date.parse(b.created_at) || 0));
  const locked = !!chase.phase || chase.disabled;
  const chaseWho = <div className="w-msg-who"><span className="w-spark" aria-hidden="true"><BI.jet size={13} /></span>Chase</div>;
  const regionLabel = id => id === 'document' ? 'Whole email' : (B.blocks || []).find(block => block.id === id)?.label || id;
  const evidence = (copy) => (copy.findings || copy.regressions || []).length > 0
    ? <ul>{(copy.findings || copy.regressions).map((item, index) => <li key={index}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>)}</ul> : null;
  const escalation = (item) => <React.Fragment>
    {!item.instruction && <div>Re-enter the instruction below to request Whole file scope.</div>}
    <button className="btn btn-quiet btn-sm" disabled={!chase.wired || chase.busy || chase.disabled || !(item.instruction || value.trim())}
      onClick={() => chase.escalate(item, value)}>Escalate to Whole file</button>
  </React.Fragment>;
  return (
    <div className="w-chase">
      <div className="w-chase-scroll" ref={scroller}>
        {!chase.wired && <div className="w-callout info"><BI.jet size={15} /><span>{chase.availabilityMessage || 'New Chase requests are unavailable. Retained results remain reviewable.'}</span></div>}
        {chase.draftBlock && <div className="w-callout info" data-chase-draft-block="true"><BI.warn size={15} /><span>{chase.draftBlock}</span></div>}
        <div className="w-chase-msgs">
          <div className="w-msg a" data-chase-intro="true">{chaseWho}<div className="w-msg-b">I stage edits as diffs — nothing changes the build until you accept. Pick a scope, then tell me what to fix.</div></div>
          {turns.map(({ key, op, proposal, instruction, msg }) => {
            if (msg) return <div key={key} className={'w-msg ' + msg.r} aria-label={msg.r === 'u' ? 'You' : 'Chase'}>{msg.r !== 'u' && chaseWho}<div className="w-msg-b">{msg.t}</div></div>;
            const patches = proposal?.patches || [];
            const requesterId = op?.principal?.id || proposal?.principal?.id;
            const requester = requesterId ? rwHistoryPerson(requesterId).n : 'Requester';
            const staged = proposal?.status === 'proposed';
            const response = op?.recovery_required || op?.cancellation_pending ? op.text
              : staged ? `Staged ${patches.length} patch${patches.length === 1 ? '' : 'es'} on ${patches.map(p => regionLabel(p.region_id)).join(', ')}. Nothing is applied until you accept.`
              : proposal ? `Proposal ${proposal.status}${proposal.revision ? ' as ' + proposal.revision : ''}.` : op?.text;
            return <React.Fragment key={key}>
              {(instruction || proposal?.instruction) && <div className="w-msg u" aria-label={requester} data-chase-question={key}><div className="w-msg-b">{instruction || proposal.instruction}</div></div>}
              <div className="w-msg a" data-chase-operation={op?.operation_id} data-chase-status={op?.status}>
                {chaseWho}
                {!staged && <div className="w-meta">{(op?.created_at || proposal?.created_at) ? rwAgo(op?.created_at || proposal?.created_at) : 'Time unavailable'} · {op?.scope?.kind === 'document' || proposal?.scope?.kind === 'document' ? 'Whole file' : (op?.scope?.region_ids || proposal?.scope?.region_ids || []).map(regionLabel).join(', ') || 'Scope unavailable'}</div>}
                <div className="w-msg-b">
                  {op?.answer ? <div data-chase-answer={op.operation_id}>
                    <div className="w-chase-answer-text">{op.answer.answer}</div>
                    <p className="w-chase-answer-note">{op.stale ? 'Historical answer — the email has changed' : 'Current saved version'} · Explanation only; nothing changed.</p>
                  </div> : <div role="status" title={op?.pending ? op.elapsedSource === 'created' ? 'Elapsed since the request was created' : 'Elapsed since this tab observed the operation' : undefined}>{response}{op?.pending && <React.Fragment> · {op.scope?.kind === 'document' ? 'Whole file' : op.scope?.region_ids?.map(regionLabel).join(', ') || 'Saved scope'}<span className="w-dots" aria-hidden="true"><i></i><i></i><i></i></span></React.Fragment>}</div>}
                  {op?.copy.detail && <p>{op.copy.detail}</p>}
                  {op && evidence(op.copy)}
                  {op?.pending && !op.cancellation_pending && <button className="btn btn-quiet btn-sm" disabled={locked} onClick={() => chase.cancel(op.operation_id)}>Cancel</button>}
                  {op?.copy.action === 'escalate' && escalation(op)}
                </div>
                {!staged && <details className="w-chase-details"><summary>Request details</summary>
                  <div className="w-meta">{op?.status || proposal?.status} · {key}</div>
                  <div>{(op?.scope?.region_ids || proposal?.scope?.region_ids || []).map(id => {const block = B.blocks?.find(b => b.id === id);return <div key={id}>{block?.identity === 'authored' ? `${block.anchor_attr || 'data-region'}="${id}"` : 'Anchor unavailable · ' + id}</div>;})}</div>
                  {op?.answer && <React.Fragment><div>Explanation for {op.answer.base.revision} · {op.answer.scope.kind === 'document' ? 'whole email' : op.answer.scope.region_ids.map(regionLabel).join(', ')}</div>
                    <div>Advisory answer only. No edits, QA verdict, approval or variant rules were created.</div></React.Fragment>}
                  {proposal && <React.Fragment><div>{proposal.explain}</div>{(proposal.patches || []).map(patch => <div className="w-chase-patch-detail" key={patch.region_id}><b>{regionLabel(patch.region_id)}</b><div>{patch.reason}</div></div>)}</React.Fragment>}
                </details>}
              </div>
            </React.Fragment>;
          })}
        </div>
        {!!stagedPatches.length && <section className="w-staged" aria-label="Staged patches">
          <div className="w-staged-h"><BI.jet size={13} style={{ color: 'var(--brand-orange)' }} />Staged patches<span className="w-pill-ok">{stagedPatches.length}</span><span className="w-grow" />
            <button className="w-chip" style={{ padding: '6px 10px', fontSize: 11.5 }} disabled={locked || chase.acceptBlocked || !chase.canAccept || chase.queue.some(patch => patch.stale) || (proposals.length > 1 && (!chase.proposalSet?.enabled || !chase.acceptAll))}
              title={proposals.length > 1 ? chase.proposalSet?.reason || 'Accept all staged patches as one revision' : chaseAcceptLabel(proposals[0].patches)}
              onClick={() => proposals.length === 1 ? chase.accept(proposals[0].proposal_id) : chase.acceptAll()}><BI.check size={11} /> Accept all</button>
          </div>
          {stagedPatches.map(({proposal,patch,queued}) => <div className={'w-patch' + (chase.selectedPatch?.pid === proposal.proposal_id && chase.selectedPatch?.blockId === patch.region_id ? ' sel' : '')} key={proposal.proposal_id+':'+patch.region_id} data-chase-proposal={proposal.proposal_id} data-chase-patch-region={patch.region_id}>
            <div className="w-staged-row">
              <button className="w-staged-row w-chase-proposal-link" data-chase-view-diff={proposal.proposal_id}
                aria-label="View diff" title="Open this diff full-size in the canvas" disabled={!queued}
                onClick={() => {chase.selectPatch(queued);onViewDiff?.(queued.blockId);}}><BI.code size={13} /><span className="w-patch-n" style={{fontSize:12.5}}>{regionLabel(patch.region_id)}</span></button>
              <button className="btn btn-quiet btn-sm" disabled={locked} onClick={() => chase.discard(proposal.proposal_id)} aria-label="Discard" title="Discard"><BI.x size={12} /></button>
              <button className="btn btn-primary btn-sm" aria-label={chaseAcceptLabel(proposal.patches)} title={chaseAcceptLabel(proposal.patches)} disabled={locked || !chase.canAccept || chase.acceptBlocked || !queued || chase.queue.some(p => p.pid === proposal.proposal_id && p.stale)} onClick={() => chase.accept(proposal.proposal_id)}><BI.check size={12} /> Accept</button>
            </div>
          </div>)}
          <div className="w-bar-note" style={{fontSize:11.5,padding:'2px 2px 8px'}}>Click a patch to read its diff full-size in the canvas.</div>
        </section>}
        {chase.phase === 'accept' && <div role="status" className="w-bar-note">Accepting proposal · replaying and running QA…</div>}
        {chase.error && <div role="alert" className="w-callout info" data-chase-error={chase.error.code}>
          <div><b>{chase.error.title}</b>{chase.error.detail && <div>{chase.error.detail}</div>}{evidence(chase.error)}
            {chase.error.action === 'escalate' && escalation(chase.error)}
          </div>
        </div>}
        {chase.listError && <div role="alert" className="w-callout info">
          <div>Could not refresh Chase: {chase.listError.title}
            <button className="btn btn-quiet btn-sm" onClick={chase.refresh}>Refresh proposals</button>
          </div>
        </div>}
        {!!chase.retainedProposals?.length && <section className="w-staged" aria-label="Retained Chase proposals">
          <div className="w-staged-h"><BI.clock size={13}/>Proposal history<span className="w-pill-ok">{chase.retainedProposals.length}</span></div>
          {chase.retainedProposals.map(proposal=><div key={proposal.proposal_id} className="w-patch w-chase-history" data-chase-retained={proposal.proposal_id}>
            <button type="button" className="w-chase-history-open" aria-label={'View '+proposal.status+' proposal'} onClick={() => setOpenProposalId(proposal.proposal_id)}><BI.code size={13}/><span className="w-chase-history-preview">{(proposal.patches||[]).map(patch=>regionLabel(patch.region_id)).join(', ')||'Proposal details'}</span><span className="w-chase-history-status">{proposal.status}</span></button>
          </div>)}
        </section>}
        {chase.retainedProposalId&&!chase.retainedProposals?.some(item=>item.proposal_id===chase.retainedProposalId)&&!proposals.some(item=>item.proposal_id===chase.retainedProposalId)&&<p className="w-bar-note">The linked proposal is not available in the current retained response. Refresh proposals to check again.</p>}
      </div>
      {openProposal && ReactDOM.createPortal(<RwChaseProposalDialog key={openProposal.proposal_id} proposal={openProposal} B={B} chase={chase} job={job} onClose={closeProposal}/>, document.body)}
      <div className="w-chase-f">
        <div className="w-scopes" role="group" aria-label="Chase scope">
          {RW_CHASE_SCOPES.map((scope) => <button key={scope.key} className={intent === 'edit' && !minorPresetActive && validScope === scope.key ? 'on' : ''} aria-pressed={intent === 'edit' && !minorPresetActive && validScope === scope.key} disabled={locked || chase.busy || scope.key === 'region' && !regionAvailable}
            onClick={() => {setIntent('edit');setMinorPreset(false);chase.setScope(scope.key);}} title={scope.key === 'region' && !regionAvailable ? 'region scope needs a builder-anchored block (RWB-1)' : scope.title}>{scope.label}</button>)}
          <button className={intent === 'edit' && minorPresetActive ? 'on' : ''} aria-pressed={intent === 'edit' && minorPresetActive} disabled={!minors.enabled || locked || chase.busy} title={minors.enabled ? `${minors.count} authored minor findings` : 'Requires 1–16 authored minor findings'}
            onClick={() => {if (!minors.enabled) return;minorSelection.current = minorKey;setIntent('edit');setMinorPreset(true);chase.setRegionIds(minors.regionIds);if (minors.scope === 'region') B.setSel(minors.regionIds[0]);chase.setScope(minors.scope);}}>All minor</button>
          <button className={intent === 'explain' ? 'on' : ''} aria-pressed={intent === 'explain'} disabled={locked || chase.busy}
            onClick={() => setIntent('explain')}>Explain only</button>
        </div>
        <div className="w-bar-note" style={{ fontSize: 12 }}>
          {minorPresetActive && <span>All minor findings · {minors.count}<br /></span>}
          {intent === 'explain' ? <React.Fragment>Answers only — explain {validScope === 'multi' ? 'the selected blocks' : validScope === 'region' && B.cur ? B.cur.label : 'the whole email'} using saved context. Nothing will be changed.</React.Fragment> : validScope === 'multi'
            ? <React.Fragment>Edits arrive as a diff you accept · scope <b>Selected blocks</b></React.Fragment> : validScope === 'region'
            ? <React.Fragment>Edits arrive as a diff you accept · scope <b>{B.cur ? B.cur.label : 'the selected region'}</b></React.Fragment>
            : !regionAvailable
              ? <React.Fragment><BI.warn size={13} /> {chase.documentScopeExplicit ? 'Whole file' : 'Choose Whole file to send'} — {B.cur ? 'this section is a derived overlay, not an authored region' : 'select a builder-anchored block for region scope'}</React.Fragment>
              : <React.Fragment>Edits arrive as a diff you accept · scope <b>Whole file</b></React.Fragment>}
        </div>
        <div className="w-chase-prompts" role="group" aria-label="Chase quick prompts">{chaseQuickPrompts(validScope, B.cur, intent).map(prompt => <button type="button" className="w-chip" key={prompt} disabled={locked || chase.busy || chase.acceptBlocked || !chase.wired || selectionInvalid}
          onClick={() => quickAsk(prompt)}>{prompt}</button>)}</div>
        <div className="w-ask">
          <input value={value} maxLength={4000} disabled={chase.busy || chase.disabled || !chase.wired} placeholder={intent === 'explain' ? 'Ask Chase about this build…' : 'Tell Chase what to change…'} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => {if (e.key === 'Enter') go();}} />
          <button className="btn btn-primary btn-sm" disabled={chase.busy || chase.disabled || chase.acceptBlocked || selectionInvalid || scopeNeedsConfirmation || !value.trim() || !chase.wired} onClick={() => go()} aria-label="Send to Chase" title={!chase.wired ? chase.availabilityMessage || RW_CHASE_GATE : scopeNeedsConfirmation ? 'Choose Whole file to confirm the write scope' : chase.draftBlock || 'Send to Chase'}><BI.send size={15} /></button>
        </div>
      </div>
    </div>
  );
}

const PEOPLE = {
  jd: { n: 'You', r: 'Dev', c: '#1F5486', i: 'JD', self: true },
  priya: { n: 'Priya Raman', r: 'SME', c: '#7A5A05', i: 'PR' },
  marcus: { n: 'Marcus Lin', r: 'Team lead', c: '#2F6B45', i: 'ML' },
  aisha: { n: 'Aisha Rao', r: 'Dev · Synchrony', c: '#4B22B8', i: 'AR' },
  chase: { n: 'Chase', r: 'AI', c: '#A34A02', i: 'CH' },
};
const RW_LIVE_PEOPLE = {
  chase: { n: 'Chase', r: 'AI', c: '#A34A02', i: 'CH' },
};
const RW_HANDLES = ['jd', 'priya', 'marcus', 'aisha', 'chase'];
const SEED_THREADS = [
  {
    id: 't1', jobId: null, revisionDigest: null, regionId: 'ratetable', author: 'priya', role: 'SME',
    body: 'Legal signed off on the APR figures. @jd can you confirm the smallest row is legible at 125% zoom? Compliance flagged exactly this last quarter.',
    mentions: ['jd'], open: true, createdAt: Date.now() - 2 * 60 * 60 * 1000,
    replies: [{ id: 't1-r1', author: 'marcus', role: 'Team lead', body: 'Same note came out of the SBS review. If it needs a re-export I want to know before Thursday’s send.', mentions: [], createdAt: Date.now() - 60 * 60 * 1000 }],
  },
  {
    id: 't2', jobId: null, revisionDigest: null, regionId: 'footer', author: 'marcus', role: 'Team lead',
    body: '@aisha the disclaimer is #343741 here but #3B3C43 in the Vancity build. Which one is the current SBS spec?',
    mentions: ['aisha'], open: true, createdAt: Date.now() - 3 * 60 * 60 * 1000, replies: [],
  },
  {
    id: 't3', jobId: null, revisionDigest: null, regionId: 'cta', author: 'aisha', role: 'Dev · Synchrony',
    body: 'utm_medium=email is on the CTA and matches the tracking sheet. Closing this one.',
    mentions: [], open: false, createdAt: Date.now() - 24 * 60 * 60 * 1000, replies: [],
  },
];

function rwMentions(text) {
  return [...new Set((String(text).match(/@([a-z0-9](?:[a-z0-9._-]*[a-z0-9])?)/gi) || []).map((value) => value.slice(1).toLowerCase()))];
}

function rwInitials(name) {
  return String(name || 'you').split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('') || 'YO';
}

function rwAgo(value) {
  if (typeof value === 'string' && /ago$|^just now$/i.test(value)) return value;
  const time = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(time)) return 'just now';
  const seconds = Math.max(0, Math.round((Date.now() - time) / 1000));
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return minutes + 'm ago';
  const hours = Math.round(minutes / 60);
  return hours < 24 ? hours + 'h ago' : Math.round(hours / 24) + 'd ago';
}

function Mentioned({ text, people }) {
  const directory = people || PEOPLE;
  const parts = String(text).split(/(@[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?)/gi);
  return (
    <React.Fragment>
      {parts.map((part, index) => {
        const handle = part[0] === '@' ? part.slice(1).toLowerCase() : null;
        if (handle && directory[handle]) return <span key={index} className={'w-mention' + (handle === 'chase' ? ' chase' : '')}>@{handle === 'jd' ? 'you' : handle}</span>;
        return part;
      })}
    </React.Fragment>
  );
}

function RW_Who({ author, role, createdAt, people }) {
  const directory = people || PEOPLE;
  const known = directory[author] || Object.values(directory).find((person) => person.n === author);
  const person = known || { n: author || 'A teammate', r: role || 'Reviewer', i: rwInitials(author) };
  const displayRole = rwCommentRole(role || person.r);
  const color = person.self ? '#1F5486' : rwCommentColor(role || person.r);
  return (
    <div className="w-cm-who">
      <span className="w-av" style={{ background: color }}>{person.i}</span>
      <span className="w-cm-n">{person.n}</span>
      <span className="w-role" style={{ color, background: 'color-mix(in srgb,' + color + ' 12%, #fff)' }}>{displayRole}</span>
      <span className="w-grow"></span>
      {createdAt && <span className="w-cm-at">{rwAgo(createdAt)}</span>}
    </div>
  );
}

function rwCommentRole(role) {
  const key = String(role || '').trim().toLowerCase().replace(/[ _-]+/g, '_');
  return ({ sme: 'SME', team_lead: 'Team lead', lead: 'Team lead', dev: 'Dev', developer: 'Dev', builder: 'Dev', ai: 'AI', workspace_owner: 'Workspace owner', org_admin: 'Org admin', platform_admin: 'Platform admin' })[key] || role || 'Reviewer';
}

function rwCommentColor(role) {
  const label = rwCommentRole(role);
  return ({ SME: '#7A5A05', 'Team lead': '#2F6B45', Dev: '#4B22B8', AI: '#A34A02' })[label] || (String(label).startsWith('Dev · ') ? '#4B22B8' : '#4A463E');
}

function rwCommentCanResolve(thread, viewer, reviewSeat) {
  const id = viewer?.user?.id;
  return !!id && (thread.author === id || reviewSeat === true);
}

function rwCommentParticipants(threads, directory) {
  const people = new Map();
  const add = (author, role) => {
    if (!author) return;
    const known = directory[author] || Object.values(directory).find(person => person.n === author);
    const person = known || { n: author, i: rwInitials(author) };
    const key = person.userId || author;
    if (!people.has(key)) people.set(key, { ...person, key, r: rwCommentRole(role || person.r), c: person.self ? '#1F5486' : rwCommentColor(role || person.r) });
  };
  threads.forEach(thread => [thread, ...(thread.replies || [])].forEach(record => {
    add(record.author, record.role);
    if (record.chaseOperations?.length) add('chase', 'AI');
  }));
  return [...people.values()];
}

function rwLiveCommentPeople() {
  const directory = { ...RW_LIVE_PEOPLE };
  const currentId = window.RBAC?.viewer?.()?.user?.id;
  const add = (member, handle) => {
    if (!member) return;
    const userId = member._id || member.user_id || member.userId || member.id || null;
    const previous = userId && directory[userId];
    const role = member.role || previous?.r || (userId === currentId && window.RBAC?.roleForContext?.(window.RBAC.viewer()));
    const person = {
      n: member.name || previous?.n || 'A teammate',
      r: rwCommentRole(role),
      c: userId === currentId ? '#1F5486' : rwCommentColor(role),
      self: !!userId && userId === currentId,
      i: member.initials || rwInitials(member.name),
      userId,
    };
    if (handle) directory[String(handle).toLowerCase()] = person;
    if (userId) directory[userId] = person;
  };
  (window.MAV && Array.isArray(window.MAV.team) ? window.MAV.team : []).forEach((member) => add(member, member.id || member.ext_id));
  const current = window.MAV && window.MAV.currentUser;
  if (current) add(current, current.ext_id || current.email && current.email.split('@')[0]);
  return directory;
}

function rwLiveMentionIds(text, people) {
  return [...new Set(rwMentions(text)
    .filter((handle) => handle !== 'chase')
    .map((handle) => people[handle] && people[handle].userId)
    .filter(Boolean))];
}

function rwCommentError(error, fallback) {
  return error && error.data && error.data.error || error && error.message || fallback;
}

function useReviewCommentFeed({ isLive, jobId, revisionDigest, chase, setThreads, enabled = true }) {
  const api = window.GooseAPI;
  const liveSeams = !!(enabled && isLive && jobId && api && ['listComments', 'addComment', 'replyComment', 'resolveComment'].every(key => typeof api[key] === 'function'));
  const binding = JSON.stringify([window.MAV?.__contextKey, window.MAV?.__contextEpoch, jobId, revisionDigest]);
  const current = React.useRef(binding);current.current = binding;
  const previousBinding = React.useRef(null);
  const request = React.useRef(0), alive = React.useRef(true);
  const [state, setState] = React.useState({ status: 'idle', error: null, notice: null });
  const operationKey = JSON.stringify((chase?.operations || []).map(op => [op.operation_id, op.status, op.proposal_id, op.answer?.answer]));
  const refresh = async () => {
    if (!liveSeams || !alive.current || current.current !== binding) return null;
    const token = ++request.current;
    try {
      const result = await api.listComments(jobId);
      if (!alive.current || current.current !== binding || token !== request.current) return null;
      const next = window.GooseReviewComments.mapCommentEnvelope(result).filter(thread => thread.jobId === jobId);
      setThreads(previous => next.map(thread => ({ ...thread,
        chaseState: thread.chaseOperations?.length ? null : previous.find(item => item.id === thread.id)?.chaseState,
      })));
      setState({ status: 'ready', error: null, notice: result.chase_errors?.length ? 'Some retained Chase evidence could not be verified. Human comments are preserved; open Chase for recovery details.' : null });
      return result;
    } catch (error) {
      if (!alive.current || current.current !== binding || token !== request.current) return null;
      setState(old => ({ ...old, status: 'error', error: rwCommentError(error, 'Comments could not be loaded.') }));
      throw error;
    }
  };
  React.useEffect(() => {alive.current = true;return () => {alive.current = false;request.current++;};}, []);
  React.useEffect(() => {
    request.current++;
    if (enabled && isLive && previousBinding.current !== binding) {
      previousBinding.current = binding;
      setThreads([]);
    }
    if (!liveSeams) {setState({ status: 'fallback', error: null, notice: null });return;}
    setState({ status: 'loading', error: null, notice: null });
    refresh().catch(() => {});
    return () => {request.current++;};
  }, [liveSeams, binding, operationKey]);
  return { ...state, liveSeams, refresh, invalidate: () => {request.current++;} };
}

function CommentsTab({ threads, setThreads, B, isLive, job, session, chase, commentFeed, focusThreadId, onThreadFocused, onViewProposal, onComposeChase }) {
  const activeChase = chase || RW_ACTIVE_CHASE;   // RW-01d integration (🐓): the shell passes the shared hook (rwTabProps.chase); the module bridge is the fallback
  const [scope, setScope] = React.useState('region');
  const [draft, setDraft] = React.useState('');
  const [chaseHandoff, setChaseHandoff] = React.useState(null);
  const [anchor, setAnchor] = React.useState(true);
  const [replyTo, setReplyTo] = React.useState(null);
  const [menu, setMenu] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [chaseNotice, setChaseNotice] = React.useState(null);
  const chaseCopy = (state) => {
    if (!state) return activeChase?.availabilityMessage || 'New Chase requests are unavailable.';
    if (state.status === 'queued') return 'Chase queued · op ' + state.operation_id;
    if (state.status === 'scope_required') return 'pick a scope in the Chase tab';
    if (state.status === 'stale_revision') return 'this thread is bound to an older revision';
    if (state.status === 'rate_limited') return 'Chase is rate-limited — try again later';
    const copy = window.GooseReviewAmendments.mapAmendError(null, { code: state.reason || state.code || 'runner_unavailable' });
    return copy.title + (copy.detail ? '. ' + copy.detail : '');
  };
  const textarea = React.useRef(null);
  const threadNodes = React.useRef(new Map());
  const mounted = React.useRef(true);
  const allThreads = Array.isArray(threads) ? threads : [];
  const jobId = job && (job.id || job._id) || null;
  const revisionDigest = isLive
    ? session && session.revision_digest || null
    : job && (job.revisionDigest || job.revision_digest) || null;
  const api = window.GooseAPI;
  const localFeed = useReviewCommentFeed({ isLive, jobId, revisionDigest, chase: activeChase, setThreads, enabled: !commentFeed });
  const feed = commentFeed || localFeed;
  const liveSeams = feed.liveSeams, loadState = feed.status;
  const commentPeople = isLive ? rwLiveCommentPeople() : PEOPLE;
  const liveHandles = isLive && window.MAV && Array.isArray(window.MAV.team)
    ? window.MAV.team.map((member) => String(member.id || member.ext_id || '').toLowerCase()).filter((handle) => /^[a-z0-9][a-z0-9._-]*$/.test(handle))
    : [];
  const commentHandles = isLive ? [...new Set([...liveHandles, 'chase'])] : RW_HANDLES;

  const mappedThreads = (result) => window.GooseReviewComments && typeof window.GooseReviewComments.mapCommentEnvelope === 'function'
    ? window.GooseReviewComments.mapCommentEnvelope(result)
    : [];
  const refreshThreads = feed.refresh;

  const retainedChase = record => (record.chaseOperations || []).map(op => <div className="w-reply w-comment-chase" key={op.operation_id} data-comment-chase-operation={op.operation_id}>
    <RW_Who author="chase" createdAt={op.created_at} />
    <div className="w-meta">{op.proposal_status || op.status} · {op.base_revision}</div>
    {op.answer && <p style={{whiteSpace:'pre-wrap'}}>{op.answer}</p>}
    {op.reason && <p>{chaseCopy(op)}</p>}
    {op.proposal_id && <button type="button" className="w-cm-act strong" onClick={() => onViewProposal?.(op.proposal_id)} disabled={!onViewProposal}>View retained proposal</button>}
    <small className="w-meta">Linked to this saved comment. Acceptance and approval remain separate.</small>
  </div>);

  React.useEffect(() => {
    mounted.current = true;
    return () => {mounted.current = false;};
  }, []);

  React.useEffect(() => {
    if (!isLive && !allThreads.length) {
      setThreads(SEED_THREADS.map((thread) => ({
        ...thread,
        jobId,
        replies: thread.replies.map((reply) => ({ ...reply })),
      })));
    }
  }, [isLive, jobId]);

  React.useEffect(() => {
    const focused = focusThreadId && allThreads.find((thread) => thread.id === focusThreadId);
    if (!focused) return;
    if (scope !== 'all' && focused.regionId !== B.sel) {
      setScope('all');
      return;
    }
    const node = threadNodes.current.get(focusThreadId);
    if (!node) return;
    const focus = () => {
      if (typeof node.scrollIntoView === 'function') node.scrollIntoView({ block: 'center', behavior: 'smooth' });
      if (onThreadFocused) onThreadFocused(focusThreadId);
    };
    const frame = window.requestAnimationFrame ? window.requestAnimationFrame(focus) : window.setTimeout(focus, 0);
    return () => {
      if (window.cancelAnimationFrame && window.requestAnimationFrame) window.cancelAnimationFrame(frame);
      else window.clearTimeout(frame);
    };
  }, [focusThreadId, scope, allThreads.length, B.sel]);

  React.useEffect(() => {
    if (!focusThreadId) return;
    setReplyTo(null);
    setMenu(null);
  }, [focusThreadId]);

  const boundThreads = allThreads.filter((thread) => !isLive || thread.jobId === jobId);
  const shown = boundThreads.filter((thread) => scope === 'region' ? thread.regionId === B.sel : true);
  const openCount = boundThreads.filter((thread) => thread.open).length;
  const label = (id) => ((B.blocks || []).find((block) => block.id === id) || {}).label || id || 'whole build';
  const hasRegion = (id) => !!(id && (B.blocks || []).some((block) => block.id === id));
  const viewer = window.RBAC && window.RBAC.viewer && window.RBAC.viewer();
  const canResolve = thread => !isLive || liveSeams && rwCommentCanResolve(thread, viewer, window.RBAC?.requireRole?.('review'));
  const participants = rwCommentParticipants(boundThreads, commentPeople);
  const author = viewer && viewer.user && viewer.user.name || 'you';
  const role = viewer && window.RBAC.roleForContext ? (window.RBAC.roleForContext(viewer) || 'Reviewer') : 'Reviewer';

  const onDraft = (value) => {
    setDraft(value);
    const match = value.slice(0, textarea.current ? textarea.current.selectionStart : value.length).match(/@([a-z0-9._-]*)$/i);
    setMenu(match ? commentHandles.filter((handle) => handle.startsWith(match[1].toLowerCase())) : null);
  };
  const pick = (handle) => {
    setDraft((value) => value.replace(/@([a-z0-9._-]*)$/i, '@' + handle + ' '));
    setMenu(null);
    if (textarea.current) textarea.current.focus();
  };
  const appendReply = (threadId, reply) => setThreads((items) => items.map((thread) => thread.id === threadId ? { ...thread, replies: [...thread.replies, reply] } : thread));
  const post = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    const parentThread = replyTo ? allThreads.find((thread) => thread.id === replyTo) : null;
    const regionId = parentThread ? parentThread.regionId : (anchor ? B.sel : null);
    if (liveSeams) {
      if (loadState !== 'ready') {
        setError('Waiting for comments to finish loading.');
        return;
      }
      if (!replyTo && !revisionDigest) {
        setError('Waiting for the current revision before this comment can be saved.');
        return;
      }
      feed.invalidate();
      setBusy(true);
      setError(null);
      setChaseNotice(null);
      let written = false;
      try {
        const toChase = /(^|\s)@chase\b/i.test(text);
        const mentions = rwLiveMentionIds(text, commentPeople);
        const response = replyTo
          ? await api.replyComment(jobId, replyTo, { body: text, mentions })
          : await api.addComment(jobId, { region_id: regionId, revision_digest: revisionDigest, body: text, mentions });
        written = true;
          if (toChase && mounted.current) {
            const chaseState = response && response.chase;
            setChaseNotice(chaseCopy(chaseState));
            if (chaseState?.status === 'scope_required') setChaseHandoff({ text, regionId });
            if (response.thread) {
              const received = mappedThreads({ threads: [response.thread] })[0];
              if (received) setThreads((items) => [...items.filter((thread) => thread.id !== received.id), { ...received, chaseState }]);
            }
            if (chaseState && chaseState.status === 'queued' && activeChase && activeChase.refresh) activeChase.refresh();
          }
        if (!mounted.current) return;
        setDraft('');
        setMenu(null);
        setReplyTo(null);
        let refreshError = null;
        try { await refreshThreads(); } catch (nextError) { refreshError = nextError; }
        if (refreshError) throw refreshError;
      } catch (nextError) {
        if (mounted.current) setError(written ? 'The comment was saved, but the latest thread could not be loaded.' : rwCommentError(nextError, 'The comment could not be saved.'));
      } finally {
        if (mounted.current) setBusy(false);
      }
      return;
    }
    const toChase = /@chase\b/i.test(text);
    const threadId = replyTo || 'c' + Date.now();
    const entry = { id: threadId + '-r' + Date.now(), author, role, body: text, mentions: rwMentions(text), createdAt: Date.now() };
    setDraft('');
    setMenu(null);
    if (replyTo) {
      appendReply(replyTo, entry);
      if (toChase) setChaseNotice('Chase on replies lands with the comments reply hook');
    } else {
      setThreads((items) => [{
        id: threadId,
        jobId,
        revisionDigest,
        regionId,
        author,
        role,
        body: text,
        mentions: rwMentions(text),
        open: true,
        createdAt: Date.now(),
        replies: [],
      }, ...items]);
    }
    setReplyTo(null);
    if (!toChase || replyTo) return;

    setBusy(true);
    let chaseBody = RW_CHASE_GATE;
    let patch = false;
    try {
      if (activeChase && typeof activeChase.ask === 'function') {
        const response = await activeChase.ask(text, {
          scope: regionId ? 'region' : 'document',
          blockId: regionId || B.sel,
        });
        if (activeChase.wired) {
          chaseBody = response && (response.say || response.reply) || 'Chase received the proposal request. Review it in the Chase tab before accepting any edit.';
          patch = !!(response && ((response.patches && response.patches.length) || (response.proposals && response.proposals.length)));
        } else {
          chaseBody = activeChase.availabilityMessage || activeChase.reply || RW_CHASE_GATE;
        }
      }
    } catch (error) {
      chaseBody = error && error.message ? 'Chase could not submit this proposal: ' + error.message : 'Chase could not submit this proposal.';
    }
    appendReply(threadId, { id: threadId + '-chase-' + Date.now(), author: 'chase', role: 'AI', body: chaseBody, mentions: [], createdAt: Date.now(), patch });
    setBusy(false);
  };
  const toggleOpen = async (thread) => {
    if (!canResolve(thread)) return;
    if (!liveSeams) {
      setThreads((items) => items.map((item) => item.id === thread.id ? { ...item, open: !item.open } : item));
      return;
    }
    if (busy || loadState !== 'ready') return;
    feed.invalidate();
    setBusy(true);
    setError(null);
    let written = false;
    try {
      await api.resolveComment(jobId, thread.id, !thread.open);
      written = true;
      await refreshThreads();
    } catch (nextError) {
      if (mounted.current) setError(written ? 'The comment state was saved, but the latest thread could not be loaded.' : rwCommentError(nextError, 'The comment state could not be saved.'));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <div className="w-cm">
      <div className="w-cm-bar">
        <div className="w-seg">
          <button className={scope === 'region' ? 'on' : ''} onClick={() => setScope('region')}>This block</button>
          <button className={scope === 'all' ? 'on' : ''} onClick={() => setScope('all')}>All{openCount ? ' · ' + openCount + ' open' : ''}</button>
        </div>
        <span className="w-grow" style={{ flex: 1 }}></span>
        {!!participants.length && <span className="w-cm-people" aria-label="People in these threads">
          {participants.map(person => <span key={person.key} data-comment-person={person.key} className="w-av sm" style={{ background: person.c }} title={person.n + ' · ' + person.r}>{person.i}</span>)}
        </span>}
      </div>
      {isLive && !liveSeams && <div className="w-bar-note" style={{ padding: '0 16px 10px', fontSize: 11.5, flexShrink: 0 }}>Comments are in-session until RWB-8.</div>}
      {liveSeams && !revisionDigest && <div className="w-bar-note" style={{ padding: '0 16px 10px', fontSize: 11.5, flexShrink: 0 }}>Waiting for the current revision before new threads can be saved.</div>}
      {loadState === 'loading' && <div className="w-bar-note" style={{ padding: '0 16px 10px', fontSize: 11.5, flexShrink: 0 }}>Loading comments…</div>}
      {(error || feed.error) && <div role="alert" className="w-bar-note" style={{ padding: '0 16px 10px', fontSize: 11.5, color: 'var(--rw-fail-ink)', flexShrink: 0 }}>{error || feed.error}</div>}
      {(chaseNotice || feed.notice) && <div className="w-bar-note" data-comment-chase-status="1" style={{ padding: '0 16px 10px', fontSize: 11.5, flexShrink: 0 }}>{chaseNotice || feed.notice}{chaseHandoff && onComposeChase && <button className="w-cm-act" onClick={() => {activeChase?.prepareComposer?.(chaseHandoff);onComposeChase(chaseHandoff);}}>Open scoped composer</button>}</div>}

      <div className="w-cm-list">
        {!shown.length && <div className="w-cm-empty">No comments on {label(B.sel).toLowerCase()} yet. Tag a teammate with <b>@</b>, or ask <b>@chase</b> for a proposal.</div>}
        {shown.map((thread) => (
          <div key={thread.id} className={'w-thread' + (thread.open ? '' : ' done')}
            data-comment-thread={thread.id} data-comment-focused={focusThreadId === thread.id ? '1' : undefined}
            ref={(node) => {if (node) threadNodes.current.set(thread.id, node); else threadNodes.current.delete(thread.id);}}>
            <RW_Who author={thread.author} role={thread.role} createdAt={thread.createdAt} people={commentPeople} />
            <div className="w-cm-t"><Mentioned text={thread.body} people={commentPeople} /></div>
            {thread.chaseState && <span className="w-tag" data-comment-chase={thread.chaseState.status} role="status">{chaseCopy(thread.chaseState)}</span>}
            {(thread.orphaned || thread.stale) && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {thread.orphaned && <span className="w-tag" data-comment-badge="orphaned" style={{ color: 'var(--rw-cond-ink)', background: 'var(--cond-bg)' }}>region no longer exists in this revision</span>}
              {thread.stale && <span className="w-tag" data-comment-badge="stale" style={{ color: 'var(--rw-cond-ink)', background: 'var(--cond-bg)' }}>written against an older revision</span>}
            </div>}
            <div className="w-cm-meta">
              {hasRegion(thread.regionId)
                ? <button className="w-cm-anchor" onClick={() => B.setSel(thread.regionId)} title="Select this region">{label(thread.regionId)}</button>
                : <span className="w-cm-anchor gen">{thread.regionId ? label(thread.regionId) + ' · region unavailable' : 'whole build'}</span>}
              {!thread.open && <span className="w-cm-res"><BI.check size={11} /> resolved</span>}
              <span className="w-grow" style={{ flex: 1 }}></span>
              <button className="w-cm-act" onClick={() => { setReplyTo(thread.id); if (textarea.current) textarea.current.focus(); }}>Reply</button>
              {canResolve(thread) && <button className="w-cm-act" disabled={busy || (liveSeams && loadState !== 'ready')} onClick={() => toggleOpen(thread)}>{thread.open ? 'Resolve' : 'Reopen'}</button>}
            </div>
            {retainedChase(thread)}
            {(thread.replies || []).map((reply) => (
              <div key={reply.id} className="w-reply">
                <RW_Who author={reply.author} role={reply.role} createdAt={reply.createdAt} people={commentPeople} />
                <div className="w-cm-t"><Mentioned text={reply.body} people={commentPeople} /></div>
                {retainedChase(reply)}
                {reply.patch && <button className="w-cm-act strong" onClick={() => hasRegion(thread.regionId) && B.setSel(thread.regionId)}><BI.code size={11} /> proposal staged — open Chase to review</button>}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="w-cm-composer">
        {replyTo && <div className="w-cm-replying">Replying in thread<span className="w-grow" style={{ flex: 1 }}></span><button className="w-cm-act" onClick={() => setReplyTo(null)}>Cancel</button></div>}
        {menu && !!menu.length && (
          <div className="w-men">
            {menu.map((handle) => <button key={handle} onClick={() => pick(handle)}><span className="w-av sm" style={{ background: commentPeople[handle].c }}>{commentPeople[handle].i}</span><b>@{handle}</b><span className="w-men-r">{commentPeople[handle].r}</span></button>)}
          </div>
        )}
        <textarea
          ref={textarea}
          className="w-in w-cm-in"
          rows={2}
          value={draft}
          disabled={busy}
          placeholder={busy ? (liveSeams ? 'Saving comment…' : 'Chase is drafting a proposal…') : 'Comment, @tag a teammate, or ask @chase to fix it…'}
          onChange={(e) => onDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) post(); }}
        />
        <div className="w-cm-foot">
          <button className={'w-toggle' + (anchor ? ' on' : '')} onClick={() => setAnchor((value) => !value)} title="Anchor this comment to the selected region"><i>{anchor && <BI.check size={11} />}</i> {anchor ? label(B.sel) : 'Whole build'}</button>
          <span className="w-grow" style={{ flex: 1 }}></span>
          <span className="w-cm-hint">⌘↵</span>
          <button className="btn btn-primary btn-sm" disabled={busy || !draft.trim() || (liveSeams && loadState !== 'ready')} onClick={post}>{busy ? (liveSeams ? 'Saving…' : 'Working…') : (replyTo ? 'Reply' : 'Comment')}</button>
        </div>
      </div>
    </div>
  );
}

const RW_HISTORY_KIND = {
  base: { label: 'pipeline', icon: 'layers', color: 'var(--muted)' },
  field: { label: 'field', icon: 'sliders', color: 'var(--running)' },
  manual: { label: 'field', icon: 'sliders', color: 'var(--running)' },
  code: { label: 'code', icon: 'code', color: 'var(--ink-2)' },
  chase: { label: 'Chase', icon: 'jet', color: 'var(--brand-orange)' },
  revert: { label: 'revert', icon: 'undo', color: 'var(--cond)' },
};

function rwRevisionReturnedUrl(url) {
  if (!url || /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i.test(url)) return url;
  const base = window.GooseAPI && window.GooseAPI.base || '';
  return base && url.startsWith('/') ? base.replace(/\/$/, '') + url : url;
}

function rwRevisionHtmlUrl(envelope) {
  return rwRevisionReturnedUrl(envelope && envelope.urls && envelope.urls.html);
}

async function rwFetchRevisionHtml(url) {
  if (typeof url !== 'string' || !url) throw new Error('Revision HTML URL is unavailable.');
  const response = await fetch(url, { credentials: 'include' });
  const text = await response.text();
  if (!response.ok) {
    let data = null;
    try {data = text ? JSON.parse(text) : null;} catch (_) {}
    const error = new Error(data && data.error || 'HTTP ' + response.status);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return text;
}

function rwRevisionAssetUrl(envelope, relative) {
  const source = String(relative || '').replace(/^03_execution\//, '').replace(/^\.\//, '');
  if (!source.startsWith('images/')) return null;
  const suffix = source.slice('images/'.length).split('/').map(encodeURIComponent).join('/');
  const returned = envelope && envelope.urls && envelope.urls.assets_base;
  if (returned) return rwRevisionReturnedUrl(returned) + suffix;
  return null;
}

// History excerpts are centered on changes, not the document's first lines.
// Keep the shared draft diff unchanged; retain a small context around each edit.
function rwRevisionDiffExcerpt(before, after, max = 160) {
  const rows = rwDiff(before, after, Number.MAX_SAFE_INTEGER);
  const included = new Set();
  rows.forEach((row, index) => {
    if (row.t === 'ctx') return;
    for (let i = Math.max(0, index - 3); i <= Math.min(rows.length - 1, index + 3); i++) included.add(i);
  });
  const lines = [];
  let previous = -1;
  for (const index of included) {
    if (index > previous + 1) lines.push({ t: 'ctx', s: '⋯ unchanged lines omitted ⋯' });
    lines.push(rows[index]);
    previous = index;
  }
  return { lines: lines.slice(0, max), truncated: lines.length > max };
}

function rwHistoryPerson(author) {
  const id = typeof author === 'string' ? author : author?.user_id || author?.kind;
  if (id === 'pipeline' || id === 'engine') return { n: 'Pipeline', c: '#4A463E', i: 'PL' };
  if (!id || id === 'unknown') return { n: 'Unknown author', c: '#4A463E', i: null };
  if (id === 'chase') return RW_LIVE_PEOPLE.chase;
  const people = rwLiveCommentPeople();
  if (id && people[id]) return people[id];
  const current = window.MAV?.currentUser;
  const name = id === 'jd' ? current?.name || 'You' : id || 'Pipeline';
  return { n: name, c: '#4A463E', i: rwInitials(name) };
}

function rwDraftHistoryDiff(entry, current) {
  const blockHtml = (snapshot, id) => {
    const range = snapshot?.blocks?.find(block => block.id === id)?.range;
    const html = snapshot?.snap;
    return typeof html === 'string' && Number.isInteger(range?.start) && Number.isInteger(range?.end)
      && range.start >= 0 && range.end > range.start && range.end <= html.length
      ? html.slice(range.start, range.end) : null;
  };
  const before = entry.blockId ? blockHtml(entry, entry.blockId) : null;
  const after = entry.blockId ? blockHtml(current, entry.blockId) : null;
  const bound = before !== null && after !== null;
  return { ...rwRevisionDiffExcerpt(bound ? before : String(entry.snap || ''), bound ? after : String(current.snap || ''), 60),
    label: bound ? entry.label || entry.blockId : 'Document excerpt · block range unavailable' };
}

// One mount per opening: loading/error transitions must not steal focus or
// replace the opener. Busy restores stay modal until the request settles.
function RwHistoryDialog({ label, onClose, busy = false, children, ...attributes }) {
  const dialog = React.useRef(null);
  const state = React.useRef({ onClose, busy }); state.current = { onClose, busy };
  React.useEffect(() => {
    const element = dialog.current, previous = document.activeElement;
    element.focus();
    const focusable = () => [...element.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),summary,[tabindex="0"]')]
      .filter(node => !node.closest('[inert],[hidden]') && node.getClientRects().length && getComputedStyle(node).visibility === 'visible');
    const key = event => {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopPropagation();
        if (!state.current.busy) state.current.onClose();
      }
      if (event.key !== 'Tab') return;
      const nodes = focusable(), first = nodes[0], last = nodes.at(-1), active = document.activeElement;
      if (!first) { event.preventDefault(); element.focus(); }
      else if (!element.contains(active) || active === element) { event.preventDefault(); (event.shiftKey ? last : first).focus(); }
      else if (event.shiftKey && active === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
    };
    const contain = event => { if (!element.contains(event.target)) element.focus(); };
    document.addEventListener('keydown', key, true);
    document.addEventListener('focusin', contain);
    return () => {
      document.removeEventListener('keydown', key, true);
      document.removeEventListener('focusin', contain);
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return <div className="w-lb w-review-theme" style={{ cursor: 'default' }} onClick={event => {
    if (event.target === event.currentTarget && !state.current.busy) state.current.onClose();
  }}>
    <div className="w-fw" ref={dialog} role="dialog" aria-modal="true" aria-label={label} aria-busy={busy || undefined} tabIndex={-1} {...attributes}>{children}</div>
  </div>;
}

function HistoryTab({ B, revisions, job, session, canRestore = true, onHead }) {
  const [scope, setScope] = React.useState('all');
  const [open, setOpen] = React.useState(null);
  const [confirm, setConfirm] = React.useState(null);
  const [reason, setReason] = React.useState('');
  const [revisionRows, setRevisionRows] = React.useState(() => Array.isArray(revisions) ? revisions : []);
  const [nextCursor, setNextCursor] = React.useState(null);
  const [pageBusy, setPageBusy] = React.useState(false);
  const pageRequest = React.useRef(null);
  const listRequest = React.useRef(0);
  const [head, setHead] = React.useState(() => session && session.revision || null);
  const [revisionState, setRevisionState] = React.useState('idle');
  const [revisionError, setRevisionError] = React.useState(null);
  const [revisionResult, setRevisionResult] = React.useState(null);
  const [snapshot, setSnapshot] = React.useState(null);
  const [comparison, setComparison] = React.useState(null);
  const compareRequest = React.useRef(0);
  const [restoreBusy, setRestoreBusy] = React.useState(false);
  const [qaReport, setQaReport] = React.useState(null);
  const qaRequest = React.useRef(0);
  const snapshotRequest = React.useRef(0);
  const localHistory = Array.isArray(B.hist) ? B.hist : [];
  // The retained head represents the draft's starting point; do not list it twice.
  const draftEntries = localHistory.filter(entry => entry.kind !== 'base' || !revisionRows.length);
  const drafts = draftEntries.slice().reverse().filter(entry => scope === 'all' || entry.blockId === B.sel || entry.kind === 'base');
  const visibleRevisions = revisionRows.slice().reverse().filter(row => scope === 'all'
    || row.changes?.attribution === 'verified' && row.changes.affected_region_ids.includes(B.sel));
  const current = B.hist && B.hist[B.hist.length - 1];
  const hasLocalChanges = localHistory.length > 1;
  const versionCount = draftEntries.length + revisionRows.length;
  const hasRegion = (id) => !!(id && (B.blocks || []).some((block) => block.id === id));
  const jobId = job && job.id;
  const historyContext = JSON.stringify([window.MAV?.activeWorkspaceId || null, jobId, session?.revision, session?.revision_digest, session?.replay_status]);
  const liveHistoryContext = React.useRef(historyContext);
  liveHistoryContext.current = historyContext;
  const isLive = !!(job && job._id);
  const canListRevisions = isLive && !!(window.GooseAPI && typeof window.GooseAPI.listRevisions === 'function');
  const canReadRevision = isLive && !!(window.GooseAPI && typeof window.GooseAPI.readRevision === 'function');
  const canRestoreRevision = isLive && !!(window.GooseAPI && typeof window.GooseAPI.restoreRevision === 'function');
  const digestOf = (rows, revision) => {
    const row = rows.find((entry) => entry && entry.revision === revision);
    return row && (row.revision_digest || row.revisionDigest || row.digest)
      || session && session.revision === revision && session.revision_digest
      || null;
  };

  React.useEffect(() => {
    let alive = true;
    const request = ++listRequest.current;
    pageRequest.current = null;
    setNextCursor(null);
    setPageBusy(false);
    snapshotRequest.current++;
    compareRequest.current++;
    setComparison(null);
    qaRequest.current++;
    setQaReport(null);
    const fallback = Array.isArray(revisions) ? revisions : [];
    setRevisionRows(fallback);
    setHead(session && session.revision || null);
    setRevisionError(null);
    setRevisionResult(null);
    setSnapshot(null);
    if (!jobId || !canListRevisions) {
      setRevisionState('idle');
      const fallbackHead = session && session.revision || null;
      if (onHead) onHead(fallbackHead ? { revision: fallbackHead, revision_digest: digestOf(fallback, fallbackHead) } : null);
      return () => {alive = false;listRequest.current++;pageRequest.current = null;snapshotRequest.current++;qaRequest.current++;compareRequest.current++;};
    }
    setRevisionState('loading');
    window.GooseAPI.listRevisions(jobId).then((result) => {
      if (!alive || request !== listRequest.current || liveHistoryContext.current !== historyContext) return;
      const rows = Array.isArray(result) ? result : result && Array.isArray(result.revisions) ? result.revisions : [];
      const nextHead = result && result.head || session && session.revision || rows.length && rows[rows.length - 1].revision || null;
      setRevisionRows(rows);
      setNextCursor(result?.pagination?.next_cursor || null);
      setHead(nextHead);
      setRevisionState('ready');
      if (onHead) onHead(nextHead ? { revision: nextHead, revision_digest: digestOf(rows, nextHead) } : null);
    }).catch((error) => {
      if (!alive || request !== listRequest.current || liveHistoryContext.current !== historyContext) return;
      setRevisionState('error');
      setRevisionError(error && error.data && error.data.error || error.message || 'Failed to load revisions.');
    });
    return () => {alive = false;listRequest.current++;pageRequest.current = null;snapshotRequest.current++;qaRequest.current++;compareRequest.current++;};
  }, [historyContext]);

  const loadOlder = async () => {
    if (!nextCursor || !canListRevisions || pageRequest.current) return;
    const request = { context: historyContext, cursor: nextCursor, list: listRequest.current };
    pageRequest.current = request;
    setPageBusy(true);
    setRevisionError(null);
    const currentRequest = () => pageRequest.current === request && liveHistoryContext.current === request.context && listRequest.current === request.list;
    try {
      const result = await window.GooseAPI.listRevisions(jobId, { cursor: nextCursor });
      if (!currentRequest()) return;
      if (result?.head !== head || result?.head_digest !== digestOf(revisionRows, head)) {
        if (onHead && result?.head && result?.head_digest) onHead({ revision: result.head, revision_digest: result.head_digest });
        throw Error('The build moved — reload history before loading older revisions.');
      }
      const rows = result?.revisions;
      if (!Array.isArray(rows) || rows.some(row => !/^r(?:0|[1-9]\d{0,5})$/.test(row?.revision || '')
        || !/^[a-f0-9]{64}$/.test(row.revision_digest || '') || Number(row.revision.slice(1)) >= Number(request.cursor.slice(1)))) throw Error('Invalid history page — reload.');
      const merged = new Map(revisionRows.map(row => [row.revision, row]));
      for (const row of rows) {
        if (merged.has(row.revision) && merged.get(row.revision).revision_digest !== row.revision_digest) throw Error('Revision identity changed — reload history.');
        merged.set(row.revision, row);
      }
      const cursor = result?.pagination?.next_cursor || null;
      if (cursor && (!/^r(?:0|[1-9]\d{0,5})$/.test(cursor) || Number(cursor.slice(1)) >= Number(request.cursor.slice(1)))) throw Error('History cursor did not advance — reload.');
      setRevisionRows([...merged.values()].sort((a, b) => Number(a.revision.slice(1)) - Number(b.revision.slice(1))));
      setNextCursor(cursor);
    } catch (error) { if (currentRequest()) setRevisionError(error?.data?.error || error.message || 'Failed to load older revisions.'); }
    finally { if (currentRequest()) { pageRequest.current = null; setPageBusy(false); } }
  };

  React.useEffect(() => {
    if (!canRestore && confirm && typeof confirm === 'object' && !restoreBusy) setConfirm(null);
  }, [canRestore, restoreBusy]);

  const viewRevision = async (revision) => {
    if (!jobId || !canReadRevision) return;
    const request = ++snapshotRequest.current;
    setSnapshot({ revision, state: 'loading' });
    setRevisionError(null);
    try {
      const envelope = await window.GooseAPI.readRevision(jobId, revision);
      if (snapshotRequest.current !== request) return;
      setRevisionRows((rows) => rows.map((row) => row.revision === revision ? { ...row, ...envelope } : row));
      const htmlUrl = rwRevisionHtmlUrl(envelope);
      const html = await rwFetchRevisionHtml(htmlUrl);
      if (snapshotRequest.current !== request) return;
      const safeHtml = window.GooseReviewBlocks && typeof window.GooseReviewBlocks.buildDisplayDoc === 'function'
        ? window.GooseReviewBlocks.buildDisplayDoc(html, {
          artifactUrl: (relative) => rwRevisionAssetUrl(envelope, relative),
        }).doc
        : html;
      setSnapshot({ revision, state: 'ready', envelope, htmlUrl, html: safeHtml });
    } catch (error) {
      if (snapshotRequest.current !== request) return;
      setSnapshot({ revision, state: 'error', error: error && error.data && error.data.error || error.message || 'Failed to read the revision.' });
    }
  };
  const closeSnapshot = () => {snapshotRequest.current++;setSnapshot(null);};

  const readQa = async (revision, label) => {
    const request = ++qaRequest.current;
    setQaReport({ revision, label, state: 'loading' });
    try {
      const receipt = await window.GooseAPI.readRevisionQa(jobId, revision);
      if (request !== qaRequest.current) return;
      // The landed /qa route streams QaReceipt.v1 JSON, not markdown.
      setQaReport({ revision, label, state: 'ready', text: JSON.stringify(receipt, null, 2) });
    } catch (error) {
      if (request !== qaRequest.current) return;
      setQaReport({ revision, label, state: 'error', text: error && error.data && error.data.error || error.message });
    }
  };
  const closeQa = () => {qaRequest.current++;setQaReport(null);};

  const compareRevision = async (revision) => {
    const api = window.GooseAPI;
    if (!head || !canReadRevision || !api.readRevisionAssets) return;
    const request = ++compareRequest.current;
    const targetHead = head;
    setComparison({ state: 'loading', from: revision, to: targetHead });
    try {
      const read = async id => {
        const [envelope, assets] = await Promise.all([api.readRevision(jobId, id), api.readRevisionAssets(jobId, id)]);
        if (envelope.revision !== id || assets.revision !== id || envelope.revision_digest !== digestOf(revisionRows, id)) {
          throw Error('Revision identity changed — reload history before comparing.');
        }
        return { html: await rwFetchRevisionHtml(rwRevisionHtmlUrl(envelope)), manifest: assets.manifest };
      };
      const [before, after] = await Promise.all([read(revision), read(targetHead)]);
      const assets = compareAssetManifests(before.manifest, after.manifest);
      if (request !== compareRequest.current) return;
      setComparison({ state: 'ready', from: revision, to: targetHead, assets,
        htmlChanged: before.html !== after.html, ...rwRevisionDiffExcerpt(before.html, after.html) });
    } catch (error) {
      if (request === compareRequest.current) setComparison({ state: 'error', from: revision, to: targetHead, error: error.message });
    }
  };
  const closeComparison = () => {compareRequest.current++;setComparison(null);};

  const restoreRevision = async () => {
    if (!confirm || !jobId || restoreBusy || !canRestore || !canRestoreRevision) return;
    const expectedDigest = digestOf(revisionRows, head);
    if (!head || !expectedDigest) {
      setRevisionError('The current revision head is unavailable — reload.');
      return;
    }
    setRestoreBusy(true);
    setRevisionError(null);
    setRevisionResult(null);
    try {
      // The HTTP route consumes snake_case CAS fields; GooseAPI forwards this body unchanged.
      const restored = await B.bundleMutation('bundle_restore', () => window.GooseAPI.restoreRevision(jobId, confirm.revision, {
        expected_head: head,
        expected_head_digest: expectedDigest,
        reason: reason.trim() || undefined,
      }));
      if (!restored) return;
      const nextRows = [...revisionRows.filter((row) => row.revision !== restored.revision), restored];
      setRevisionRows(nextRows);
      setHead(restored.revision);
      if (onHead) onHead({ revision: restored.revision, revision_digest: restored.revision_digest || restored.digest || null });
      setRevisionResult('Restored ' + confirm.revision + ' as new head ' + restored.revision + '.');
      setConfirm(null);
      setReason('');
      snapshotRequest.current++;
      setSnapshot(null);
    } catch (error) {
      const code = error && error.data && error.data.code || error && error.code;
      setRevisionError(code === 'revision_conflict'
        ? 'the build moved — reload'
        : error && error.data && error.data.error || error.message || 'Failed to restore the revision.');
    } finally {
      setRestoreBusy(false);
    }
  };

  return (
    <div className="w-cm">
      <div className="w-cm-bar"><div className="w-seg" aria-label="History scope">
        <button className={scope === 'all' ? 'on' : ''} onClick={() => setScope('all')}>All edits</button>
        <button className={scope === 'region' ? 'on' : ''} disabled={!B.sel} onClick={() => setScope('region')}>This block</button>
      </div><span className="w-grow" /><span className="w-meta">{versionCount} version{versionCount === 1 ? '' : 's'}{nextCursor ? ' loaded' : ''}</span></div>
      <div className="w-cm-list" style={{ paddingTop: 14 }}>
          {!!drafts.length && <div className="w-sec-h">Unsaved draft checkpoints<span className="w-grow"/><span>This browser only · undo changes your draft</span></div>}
          {drafts.map((entry) => {
            const kind = RW_HISTORY_KIND[entry.kind] || RW_HISTORY_KIND.code;
            const Icon = BI[kind.icon] || BI.code;
            const isCurrent = current && entry.vid === current.vid;
            const diff = open === entry.vid && current ? rwDraftHistoryDiff(entry, current) : null;
            const person = rwHistoryPerson(entry.who);
            return (
              <div key={entry.vid} data-draft-row={entry.vid} data-history-kind={kind.label} className={'w-hist' + (isCurrent ? ' cur' : '')}>
                <div className="w-hist-line">
                  <span className="w-hist-ico" style={{ color: kind.color }}><Icon size={13} /></span>
                  <span className="w-hist-sum">{entry.summary}{entry.n > 1 && <span className="w-hist-n">×{entry.n}</span>}</span>
                  {isCurrent && <span className="w-hist-cur">current</span>}
                </div>
                <div className="w-hist-meta">
                  {person.i && <span className="w-av sm" style={{ background: person.c, marginLeft: 0 }}>{person.i}</span>}
                  <span className="w-hist-who">{person.n}</span>
                  <span className="w-hist-at">{rwAgo(entry.at)}</span>
                  <span className="w-meta">{entry.kind === 'base' ? 'starting point here' : 'unsaved here'}</span>
                  {entry.label && (hasRegion(entry.blockId)
                    ? <button className="w-cm-anchor" onClick={() => B.setSel(entry.blockId)}>{entry.label}</button>
                    : <span className="w-cm-anchor gen">{entry.label} · region unavailable</span>)}
                  <span className="w-grow" style={{ flex: 1 }}></span>
                  {!isCurrent && <button className="w-cm-act" onClick={() => setOpen(open === entry.vid ? null : entry.vid)}>{open === entry.vid ? 'Hide' : 'Diff'}</button>}
                  {!isCurrent && B.canRevertDraft !== false && (confirm === entry.vid
                    ? <React.Fragment><span className="w-hist-ask">Revert to this?</span><button className="w-cm-act" onClick={() => setConfirm(null)}>Cancel</button><button className="btn btn-primary btn-sm" onClick={() => { B.revertTo(entry.vid); setConfirm(null); setOpen(null); }}><BI.undo size={12} /> Revert</button></React.Fragment>
                    : <button className="w-cm-act" onClick={() => setConfirm(entry.vid)}><BI.undo size={11} /> Undo draft</button>)}
                </div>
                {open === entry.vid && (
                  <div className="w-hist-diff">
                    {diff?.lines.length
                      ? diff.lines.map((line, index) => <div key={index} className={'w-dl ' + line.t}><i>{line.t === 'add' ? '+' : line.t === 'del' ? '−' : ''}</i>{line.s}</div>)
                      : <div className="w-hist-same">Identical to the current draft.</div>}
                    <div className="w-hist-legend">− this draft · + current draft · {diff?.label}{diff?.truncated ? ' · limited to 60 lines; further changes omitted' : ''}</div>
                  </div>
                )}
              </div>
            );
          })}
          {!canListRevisions && visibleRevisions.map((revision, index) => {
            const digest = revision.revisionDigest || revision.revision_digest || revision.digest || revision.id || 'revision-' + (index + 1);
            const summary = revision.summary || revision.label || 'Saved revision';
            const at = revision.createdAt || revision.created_at || revision.at;
            const digestLabel = String(digest);
            const shortDigest = digestLabel.length > 18 ? digestLabel.slice(0, 12) + '…' : digestLabel;
            return <div key={digest} className="w-hist" style={{ marginBottom: 8 }}>
              <div className="w-hist-line"><span className="w-hist-ico"><BI.clock size={13} /></span><span className="w-hist-sum">{summary}</span></div>
              <div className="w-hist-meta"><span className="w-hist-who">saved</span><span className="w-hist-at">{at ? rwAgo(at) : shortDigest}</span><span className="w-grow" style={{ flex: 1 }}></span><code className="w-meta" title={digestLabel}>{shortDigest}</code></div>
            </div>;
          })}
          {canListRevisions && revisionState === 'loading' && <div className="w-bar-note" data-revisions-state="loading">Loading revision graph…</div>}
          {canListRevisions && !!revisionRows.length && <div data-revisions-list="1" className="w-history-timeline">
              <div className="w-sec-h">Saved revisions<span className="w-grow"/><span>Restore creates a new revision</span></div>
              {visibleRevisions.map((revision, index) => {
                const revisionId = revision.revision || revision.id || 'revision-' + (index + 1);
                const digest = String(revision.revision_digest || revision.revisionDigest || revision.digest || '');
                const parent = revision.parent_revision || revision.parent || '—';
                const isHead = revisionId === head;
                const hasReceipt = window.GooseReviewQa.historyQaAvailable({ ...revision, revision: revisionId }, session, isHead);
                const replayLabel = window.GooseReviewQa.historyQaLabel({ ...revision, revision: revisionId, qa_receipt_available: hasReceipt }, isHead);
                const changes = revision.changes;
                const person = rwHistoryPerson(revision.author);
                return <article className={'w-hist' + (isHead && !hasLocalChanges ? ' cur' : '')} key={revisionId + ':' + digest} data-revision-row={revisionId} data-revision-head={isHead ? '1' : undefined}>
                  <div className="w-hist-line"><span className="w-hist-ico"><BI.clock size={13} /></span><span className="w-hist-sum">{revision.reason || (changes?.change_kind === 'initial' ? 'Initial build' : changes?.change_kind === 'restore' ? 'Restored bundle' : changes?.change_kind === 'asset' ? 'Assets updated' : 'Applied changes')}</span><b className="w-meta">{revisionId}</b>{isHead && <span className="w-hist-cur">head</span>}</div>
                  <div className="w-hist-meta">{person.i && <span className="w-av sm" style={{ background: person.c, marginLeft: 0 }}>{person.i}</span>}<span className="w-hist-who">{person.n}</span><span className="w-hist-at">{revision.at ? rwAgo(revision.at) : ''}</span><span className="w-meta">saved{revision.edit_attribution ? ' · ' + (revision.edit_attribution.method === 'chase_proposal' ? 'Chase' : revision.edit_attribution.method === 'code' ? 'Code / manual' : revision.edit_attribution.method) : ''}</span>{revision.edit_attribution?.proposal_id && <span className="w-meta" title={revision.edit_attribution.proposal_id}>Proposal {revision.edit_attribution.proposal_id.slice(0, 11)}…</span>}
                    {(changes?.affected_region_ids || []).map(id => <button key={id} className="w-cm-anchor" disabled={!hasRegion(id)} onClick={() => B.setSel(id)}>{(B.blocks || []).find(b => b.id === id)?.label || id}</button>)}{changes?.document_changed && <span className="w-cm-anchor gen">Document / assets</span>}
                  </div>
                  {revision.evidence_error && <div className="w-bar-note" data-evidence-error="1">Retained evidence failed integrity checks. Keep this revision and request recovery; it cannot certify delivery.</div>}
                  <div className="w-history-actions">
                    {canReadRevision && <button className="w-cm-act" data-action="view-revision" onClick={() => viewRevision(revisionId)}>View</button>}
                    {!isHead && canReadRevision && window.GooseAPI.readRevisionAssets && <button className="w-cm-act" data-action="compare-revision" onClick={() => compareRevision(revisionId)}>Compare with head</button>}
                    {hasReceipt && window.GooseAPI && typeof window.GooseAPI.readRevisionQa === 'function' && <button className="w-cm-act" data-action="revision-qa" onClick={() => readQa(revisionId, replayLabel || 'QA report (' + revisionId + ')')}>QA report</button>}
                    {!isHead && canRestoreRevision && <button className="w-cm-act" data-action="restore-revision" disabled={!canRestore} title={!canRestore ? 'Reload before restoring a revision' : 'Restore as a new head'} onClick={() => {setConfirm(revision);setReason('');setRevisionError(null);}}>Restore</button>}
                    {isHead && revisionId === session?.revision && hasLocalChanges && B.canRevertDraft !== false && (confirm === localHistory[0].vid
                      ? <React.Fragment><span className="w-hist-ask">Revert the unsaved draft?</span><button className="w-cm-act" onClick={() => setConfirm(null)}>Cancel</button><button className="btn btn-primary btn-sm" onClick={() => {B.revertTo(localHistory[0].vid);setConfirm(null);setOpen(null);}}><BI.undo size={12} /> Revert</button></React.Fragment>
                      : <button className="w-cm-act" data-action="revert-draft" onClick={() => setConfirm(localHistory[0].vid)}><BI.undo size={11} /> Revert draft</button>)}
                  </div>
                  <details className="w-history-details"><summary>Revision details</summary><dl><dt>Parent</dt><dd>{parent}</dd><dt>Authority</dt><dd>{revision.authority_mode || '—'}</dd><dt>Approval</dt><dd>{revision.approval_state || '—'}</dd><dt>Replay</dt><dd>{revision.replay_status == null ? '—' : revision.replay_status}</dd><dt>Digest</dt><dd>{digest}</dd></dl>
                    {replayLabel && <div className="chip" data-revision-qa={revision.replay_status}>{replayLabel}</div>}
                    {changes?.attribution !== 'verified' && <p>{changes?.attribution === 'partial' ? 'Some changes are not block-attributable' : 'Block attribution unavailable'}</p>}
                    {(changes?.changed_assets || []).map(asset => <div key={asset.name}><code>{asset.name}</code> · {asset.before ? asset.after ? 'replaced' : 'removed' : 'added'}</div>)}
                  </details>
                </article>;
              })}
              {!visibleRevisions.length && !drafts.length && <div className="w-cm-empty">No verified changes for this block. Document-wide and unattributed changes remain under All edits.</div>}
          </div>}
          {canListRevisions && nextCursor && <button className="btn btn-quiet btn-sm" data-action="load-older-revisions" disabled={pageBusy} onClick={loadOlder}>{pageBusy ? 'Loading older revisions…' : 'Load older revisions'}</button>}
          {!revisionRows.length && !drafts.length && revisionState !== 'loading' && <div className="w-cm-empty">No history entries yet.</div>}
          {canListRevisions && revisionError && <div className="w-callout fail" role="alert" data-revision-result="error" style={{ marginTop: 10 }}><BI.warn size={14} /><span>{revisionError}</span></div>}
          {canListRevisions && revisionResult && <div className="w-callout pass" role="status" data-revision-result="success" style={{ marginTop: 10 }}><BI.check size={14} /><span>{revisionResult}</span></div>}
      </div>
      <div className="w-cm-composer" style={{ paddingTop: 12 }}><div className="w-bar-note" style={{ fontSize: 11.5, lineHeight: 1.5 }}>Reverting an unsaved edit restores the whole draft as a new local entry. Apply changes &amp; rerun QA to save it. Restoring a saved revision creates a new bundle revision; history is kept and approval is not inherited.</div>
        {revisionRows.some(row => row.evidence_scope === 'receipt_only') && <details className="w-history-details" data-history-evidence-scope="receipt_only"><summary>History evidence</summary>History checks HTML and recorded metadata. Asset and render integrity is checked when opening evidence, restoring or approving; this list is not certification.</details>}
      </div>
      {comparison && ReactDOM.createPortal(<RwHistoryDialog label={'Compare ' + comparison.from + ' with ' + comparison.to} onClose={closeComparison} data-bundle-comparison={comparison.state} style={{ width: 960 }}>
          <header className="w-fw-h"><div style={{ flex: 1 }}><b>Bundle · {comparison.from} → {comparison.to}</b><span>Read-only revision comparison</span></div><button className="btn btn-quiet btn-sm" aria-label="Close comparison" onClick={closeComparison}><BI.x size={14} /></button></header>
          <div className="w-fw-b" style={{ paddingBottom: 18 }}>
            {comparison.state === 'loading' && <div role="status">Loading retained HTML and asset manifests…</div>}
            {comparison.state === 'error' && <div role="alert">{comparison.error}</div>}
            {comparison.state === 'ready' && <React.Fragment>
              <div className="w-bar-note">Read-only comparison; no approval or QA is inherited. HTML {comparison.htmlChanged ? 'changed (bounded excerpt below)' : 'unchanged'}.</div>
              {comparison.htmlChanged && <div className="w-hist-diff" style={{ maxHeight: '45vh' }}>{comparison.lines.map((line, index) => <div key={index} className={'w-dl ' + line.t}><i>{line.t === 'add' ? '+' : line.t === 'del' ? '−' : ''}</i>{line.s}</div>)}</div>}
              <div className="w-hist-legend">− {comparison.from} · + {comparison.to} · Change-centered HTML excerpt{comparison.truncated ? ' limited to 160 lines; further changes omitted' : ''}</div>
              <ul>{comparison.assets.map(asset => <li key={asset.name} data-bundle-asset-change={asset.change}><b>{asset.name}</b> · {asset.change} · {asset.before?.bytes ?? '—'} → {asset.after?.bytes ?? '—'} bytes<br /><code style={{ overflowWrap: 'anywhere' }}>{asset.before?.sha256 || 'absent'} → {asset.after?.sha256 || 'absent'}</code></li>)}</ul>
              {!comparison.assets.length && <div>No assets in either retained bundle.</div>}
            </React.Fragment>}
          </div>
        </RwHistoryDialog>, document.body)}
      {canListRevisions && snapshot && ReactDOM.createPortal(<RwHistoryDialog label={'Revision ' + snapshot.revision + ' snapshot'} onClose={closeSnapshot} data-revision-snapshot={snapshot.revision} style={{ width: 960 }}>
          <header className="w-fw-h"><div style={{ flex: 1 }}><b>Snapshot · {snapshot.revision}</b><span>Read-only; the active head and draft are unchanged.</span></div><button className="btn btn-quiet btn-sm" aria-label="Close revision snapshot" onClick={closeSnapshot}><BI.x size={14} /></button></header>
          <div className="w-fw-b" style={{ paddingBottom: 18 }}>
            {snapshot.state === 'loading' && <div className="w-bar-note" role="status">Loading exact revision…</div>}
            {snapshot.state === 'error' && <div className="w-callout fail" role="alert">{snapshot.error}</div>}
            {snapshot.state === 'ready' && <React.Fragment>
              <div className="w-bar-note" style={{ marginBottom: 7 }}>Read-only · {snapshot.envelope.authority_mode || 'authority unknown'} · {snapshot.envelope.approval_state || 'approval unknown'} · replay {snapshot.envelope.replay_status || 'unknown'}</div>
              <iframe title={'Revision ' + snapshot.revision + ' snapshot'} srcDoc={snapshot.html} data-revision-html-url={snapshot.htmlUrl} sandbox="allow-same-origin" referrerPolicy="no-referrer" style={{ width: '100%', height: '60vh', minHeight: 200, border: '1px solid var(--line)', background: '#fff' }}></iframe>
            </React.Fragment>}
          </div>
        </RwHistoryDialog>, document.body)}
      {qaReport && ReactDOM.createPortal(<RwHistoryDialog label={qaReport.label} onClose={closeQa} data-revision-qa-sheet={qaReport.revision}>
          <header className="w-fw-h"><b style={{ flex: 1 }}>{qaReport.label}</b><button className="btn btn-quiet btn-sm" aria-label="Close QA report" onClick={closeQa}><BI.x size={14} /></button></header>
          <div className="w-fw-b">{qaReport.state === 'loading' ? <span role="status">Loading QA report…</span> : <pre role={qaReport.state === 'error' ? 'alert' : undefined} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{qaReport.text}</pre>}</div>
      </RwHistoryDialog>, document.body)}
      {confirm && typeof confirm === 'object' && ReactDOM.createPortal(
        <RwHistoryDialog label={'Restore ' + confirm.revision + '?'} busy={restoreBusy} onClose={() => setConfirm(null)} data-restore-sheet={confirm.revision}>
            <header className="w-fw-h"><div className="w-fw-ico"><BI.undo size={15} /></div><div style={{ flex: 1 }}><b>Restore {confirm.revision}?</b><span>This mints a new head from the selected snapshot. The current head is not moved or deleted.</span></div><button className="btn btn-quiet btn-sm" disabled={restoreBusy} aria-label="Close restore dialog" onClick={() => setConfirm(null)}><BI.x size={14} /></button></header>
            <div className="w-fw-b"><label className="w-lbl" htmlFor="rw-restore-reason">Reason (optional)</label><textarea id="rw-restore-reason" className="w-in" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why is this revision being restored?" />{revisionError && <div className="w-callout fail" role="alert" data-restore-result="error" style={{ marginTop: 10 }}><BI.warn size={14} /><span>{revisionError}</span></div>}</div>
            <footer className="w-fw-f"><span className="w-fw-note">Expected head: <code>{head}</code> · {String(digestOf(revisionRows, head) || '').slice(0, 8)}</span><button className="btn btn-quiet btn-sm" disabled={restoreBusy} onClick={() => setConfirm(null)}>Cancel</button><button className="btn btn-primary btn-lg" data-action="restore-confirm" disabled={restoreBusy || !canRestore} onClick={restoreRevision}><BI.undo size={14} /> {restoreBusy ? 'Restoring…' : 'Restore as new head'}</button></footer>
        </RwHistoryDialog>, document.body)}
    </div>
  );
}

const RW_FW_KIND = {
  field: { label: 'field', icon: 'sliders', why: 'A field value the engine got wrong' },
  code: { label: 'code', icon: 'code', why: 'Hand-written markup the engine did not produce' },
  chase: { label: 'Chase', icon: 'jet', why: 'A proposal you accepted — confirms the fix was right' },
};
const RW_FW_REASONS = ['Client-specific one-off', 'Experiment / not final', 'Contains sensitive copy', 'My mistake, not the engine’s'];

function ApproveSheet({ jobId, receipt, revision, revisionDigest, submitting, error, onClose, onConfirm }) {
  const [loaded, setLoaded] = React.useState({ state: 'loading', units: [] });
  const [choices, setChoices] = React.useState({});
  const [allOut, setAllOut] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [pickedReason, setPickedReason] = React.useState(null);
  const [attempt, setAttempt] = React.useState(0);
  React.useEffect(() => {
    let alive = true;
    setLoaded({ state: 'loading', units: [] });
    setChoices({});setAllOut(false);setReason('');setPickedReason(null);
    const load = async () => {
      try {
        const api = window.GooseAPI;
        if (!jobId || !api) throw new Error('Contribution history is unavailable in this lane.');
        if (typeof api.flywheelUnits !== 'function') {
          if (alive) setLoaded({ state: 'ready', units: [], unavailable: true });
          return;
        }
        const payload = await api.flywheelUnits(jobId);
        const result = window.GooseReviewContributions.rwLoadApproveSheet(payload, revisionDigest);
        if (alive) setLoaded(result);
      } catch (failure) {
        if (alive) setLoaded({ state: 'error', units: [], error: failure && failure.data && failure.data.error || failure.message || 'Failed to load contribution history.' });
      }
    };
    load();
    return () => {alive = false;};
  }, [jobId, revision, revisionDigest, attempt]);
  React.useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape' && !submitting) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);
  const edits = loaded.units;
  const excluded = loaded.excluded || [];
  const evidenceKey = entry => (entry.unit_key || entry.key) + '@' + entry.revision;
  const evidence = new Map((loaded.evidence || []).map(entry => [evidenceKey(entry), entry]));
  const evidenceDetail = entry => {
    const detail = evidence.get(evidenceKey(entry));
    return detail && <details className="w-fw-evidence" data-edit-evidence={entry.key}>
      <summary>Evidence · {entry.revision}</summary>
      {detail.recorded_fields?.length > 0 && <React.Fragment><p>Recorded field values (after). Not an individual-edit count; before values are not retained here.</p>
        {detail.recorded_fields.map((field, index) => <dl key={index}>{Object.entries(field.changes).map(([key, value]) => <React.Fragment key={key}><dt>{field.block_id} · {key}</dt><dd>{String(value ?? 'null')}</dd></React.Fragment>)}</dl>)}
      </React.Fragment>}
      <dl><dt>Method</dt><dd>{detail.method}</dd><dt>Amendment</dt><dd>{detail.amendment_id}</dd><dt>Ledger</dt><dd>{detail.ledger_digest}</dd><dt>Before HTML</dt><dd>{detail.base_html_digest}</dd><dt>After HTML</dt><dd>{detail.after_html_digest}</dd></dl>
    </details>;
  };
  const excludedNote = excluded.length > 0 && <div className="w-fw-list" data-contribution-excluded={excluded.length}>
    {excluded.map(entry => <div className="w-fw-row out" key={entry.key} data-excluded-edit={entry.key}>
      <span className="w-fw-k"><BI.jet size={11} />{entry.method === 'chase_proposal' ? 'Chase' : entry.method}</span>
      <span className="w-fw-t"><b>Excluded from Flywheel by policy</b><em>{entry.region} · {entry.revision}</em>{evidenceDetail(entry)}</span>
      <span className="w-fw-st">excluded</span>
    </div>)}
  </div>;
  const contributions = loaded.state === 'ready' ? window.GooseReviewContributions.contributionsBody(edits, allOut ? Object.fromEntries(edits.map(entry => [entry.key, false])) : choices) : [];
  const contributed = contributions.filter((entry) => entry.included).length;
  const blocked = loaded.state !== 'ready' || submitting || allOut && !pickedReason && !reason.trim();
  const chooseAll = (included) => setChoices(Object.fromEntries(edits.map((entry) => [entry.key, included])));
  const groups = new Map();
  edits.forEach((entry) => {
    if (!groups.has(entry.region)) groups.set(entry.region, []);
    groups.get(entry.region).push(entry);
  });
  const receiptState = receipt && (RW_RECEIPT_STATES[receipt.status] || { label: receipt.status || 'Run receipt', className: 'ready' });
  return (
    <div className="w-lb w-review-theme w-fw-lb" style={{ cursor: 'default' }} onClick={() => !submitting && onClose()}>
      <div className="w-fw" role="dialog" aria-modal="true" aria-label="Flywheel contribution" data-approve-sheet={loaded.state} onClick={(event) => event.stopPropagation()}>
        <header className="w-fw-h">
          <div className="w-fw-ico"><BI.retry size={15} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <b>Flywheel contribution</b>
            <span>Retain eligible corrections for governed review. These choices do not train a model or change variant rules.</span>
          </div>
          {receiptState && <div className={'w-abp ' + (receipt.status === 'candidate_ready' ? 'pass' : receipt.status === 'needs_input' ? 'cond' : 'ready')}>{receiptState.label}</div>}
          <button className="btn btn-quiet btn-sm" aria-label="Close contribution sheet" disabled={submitting} onClick={onClose}><BI.x size={14} /></button>
        </header>

        <div className="w-fw-b">
          {loaded.state === 'loading' && <div className="w-bar-note" role="status">Loading contribution history…</div>}
          {loaded.state === 'error' && <div className="w-callout fail" role="alert" data-contribution-load-error>
            <span>{loaded.error}</span><button className="btn btn-quiet btn-sm" onClick={() => setAttempt((value) => value + 1)}>Retry</button>
          </div>}
          {loaded.state === 'ready' && loaded.unverified > 0 && <div className="w-bar-note" data-contribution-unverified>{loaded.unverified} ledger members unverified — the emission may be blocked</div>}
          {loaded.state === 'ready' && loaded.evidence === null && <div className="w-bar-note" data-contribution-evidence-unavailable>Edit details unavailable. Contribution choices and approval are still available.</div>}
          {loaded.state === 'ready' && (!edits.length
            ? <React.Fragment>
              <div className="w-fw-none" data-contribution-history={loaded.unavailable ? 'unavailable' : 'ready'}><BI.check size={15} /> {loaded.unavailable ? 'Edit history is not available on this server — nothing to contribute.' : excluded.length ? 'No eligible edits since the last approval — nothing to contribute.' : 'No edits since the last approval — nothing to contribute.'}</div>
              {excludedNote}
            </React.Fragment>
            : <React.Fragment>
              <div className="w-fw-bar">
                <span className="w-fw-count"><b>{contributed}</b> of {edits.length} edit receipts selected</span>
                <span className="w-grow" style={{ flex: 1 }}></span>
                <button className="w-sq-more inline" disabled={allOut || submitting} onClick={() => chooseAll(true)}>include all</button>
                <button className="w-sq-more inline" disabled={allOut || submitting} onClick={() => chooseAll(false)}>exclude all</button>
              </div>
              <div className="w-bar-note" style={{ marginBottom: 10 }}>One choice per block includes or excludes all its eligible edit receipts. Retention is not automatic learning; approval is separate from later promotion. Evidence revocation is not available here.</div>
              {excludedNote}
              <div className={'w-fw-list' + (allOut ? ' off' : '')}>
                {Array.from(groups, ([region, entries]) => <section key={region} data-contribution-region={region}>
                  <div className="w-sec-h"><button className={'w-fw-sw' + (allOut || entries.some(entry => choices[entry.key] === false) ? '' : ' on')} role="switch" aria-checked={!allOut && entries.every(entry => choices[entry.key] !== false)} aria-label={'Contribute edits in ' + region} disabled={allOut || submitting} onClick={() => {const included = entries.some(entry => choices[entry.key] === false);setChoices(current => ({ ...current, ...Object.fromEntries(entries.map(unit => [unit.key, included])) }));}}><i /></button>{region}<span className="w-grow"/>{entries.length} eligible receipt{entries.length === 1 ? '' : 's'}</div>
                  {entries.map((entry) => {
                    const excluded = allOut || choices[entry.key] === false;
                    const kind = entry.kind === 'asset_mint' ? { label: 'asset', icon: 'image' } : evidence.has(evidenceKey(entry)) ? RW_HISTORY_KIND[evidence.get(evidenceKey(entry)).kind] : RW_HISTORY_KIND.code;
                    const Icon = BI[kind.icon] || BI.code;
                    const StatusIcon = excluded ? BI.x : BI.check;
                    return <div key={entry.key} className={'w-fw-row' + (excluded ? ' out' : '')} data-contribution-unit={entry.key}>
                      <span aria-hidden="true"><StatusIcon size={13}/></span>
                      <span className="w-fw-k"><Icon size={11} />{kind.label}</span>
                      <span className="w-fw-t"><b>{entry.label}</b><em>{region} · {entry.revision}{entry.kind === 'asset_mint' && entry.assets?.length > 0 && ' · ' + entry.assets.map((asset) => asset.name).join(', ')}</em>{entry.reason && <em data-contribution-reason>Reason: {entry.reason}</em>}{evidenceDetail(entry)}</span>
                      <span className="w-fw-st">{excluded ? 'one-off' : 'included'}</span>
                    </div>;
                  })}
                </section>)}
              </div>
              <button className={'w-fw-all' + (allOut ? ' on' : '')} role="checkbox" aria-checked={allOut} disabled={submitting} data-action="flywheel-opt-out" onClick={() => setAllOut((value) => !value)}>
                <span className={'w-box' + (allOut ? ' on' : '')}>{allOut && <BI.check size={11} />}</span>
                <span><b>Opt this whole build out of Flywheel</b><i>Exclude every edit from contribution. A reason is required and retained with this approval.</i></span>
              </button>
              {allOut && <div className="w-fw-why">
                <div className="w-fw-reasons">
                  {RW_FW_REASONS.map((item) => <button key={item} className={'w-chip' + (pickedReason === item ? ' on' : '')} disabled={submitting} onClick={() => setPickedReason(pickedReason === item ? null : item)}>{item}</button>)}
                </div>
                <input className="w-in" aria-label="Opt-out reason" required={!pickedReason} value={reason} disabled={submitting} onChange={(event) => setReason(event.target.value)} placeholder={pickedReason ? 'Add detail (optional)' : 'Reason (required)'} />
              </div>}
            </React.Fragment>)}
          {error && <div className="w-callout fail" role="alert" data-review-error={error.code} style={{ marginTop: 10, whiteSpace: 'pre-wrap' }}><span><b>{error.title}</b>{error.detail && <React.Fragment><br />{error.detail}</React.Fragment>}</span></div>}
        </div>

        <footer className="w-fw-f">
          <span className="w-fw-note"><span>Your contribution choices are recorded with this approval.{revision ? <React.Fragment> · approving <b data-approve-revision={revision}>{revision}</b>{revisionDigest ? ' / ' + String(revisionDigest).slice(0, 8) : ''}</React.Fragment> : null}</span></span>
          <button className="btn btn-quiet btn-sm" disabled={submitting} onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary btn-lg"
            disabled={blocked}
            title={loaded.state !== 'ready' ? 'Contribution history must load before approval' : 'Approve the certified revision'}
            data-action="approve-confirm"
            onClick={() => onConfirm({
              contributions,
              ...(allOut ? { flywheel_opt_out: true, opt_out_reason: [pickedReason, reason.trim()].filter(Boolean).join(' — ') } : {}),
            })}
          >
            <BI.check size={16} /> {submitting ? 'Approving…' : 'Approve & deliver'}
          </button>
        </footer>
      </div>
    </div>
  );
}

Object.assign(window, { InspectorTab, ReviewTab, ChaseTab, CommentsTab, HistoryTab, ApproveSheet, PEOPLE, SEED_THREADS, Mentioned, useReviewCommentFeed, RW_RECEIPT_STATES });
